import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import './SearchArea.css';
import '../../common/commonSelect/CheckboxDropdownSelect.css';
import MultiSelectWithCheckboxes from '../../common/MultiSelectWithCheckboxes/MultiSelectWithCheckboxes';
import { WeeklyDatePicker } from '../../common/WeeklyDatePicker/WeeklyDatePicker';
import { FreeDatePicker } from '../../common/DatePicker/FreeDatePicker';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTheme } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import type { WeeklyMaster } from './SearchArea';
import type { OptionsBase } from '../../../state/master';
import { useUserOptions } from '../../../state/master';
import { useUserProfile } from '../../../context/UserProfileContext';
import CommonSelect from '../../common/commonSelect/CommonSelect';
import type { Option as CommonSelectOption } from '../../common/commonSelect/CommonSelect';
import CheckboxDropdownSelectWithAll from '../../common/commonSelect/CheckboxDropdownSelectWithAll';
import { getFiscalYear, toFiscalYearLabel } from '../../../lib/common';
import {
  CLASSIFICATION_MARKET_TARGET_ATTR_NAME,
  FIXED_ATTR_NAME_PROJECT,
  FIXED_ATTR_NAME_POWERTRAIN,
  FIXED_ATTR_NAME_SHIMUKE,
  FIXED_ATTR_NAME_LOT,
} from '../../../constants';
import {
  coAuthorNameKey,
  mergeCoAuthorOptionsByLabel,
  parseRespondentNames,
  useCoAuthorRespondentOptions,
} from '../../../hooks/useCoAuthorRespondentOptions';
import {
  buildPreferredTeamCandidates,
  useDepartmentUserOptions,
} from '../../../hooks/useDepartmentUserOptions';
import {
  isCommonDisplayManagedAttr,
  isFreeInputManagedAttr,
  isManagedAttrVisible,
  normalizeManagedAttrNameKey,
  selectManagedAttrsForScope,
} from '../../common/utils/SearchArea.utils';
import { resolveKnownMultiSelectTokens } from '../../common/utils/reportTypeTokens';
import {
  buildWeeklyReportPublicScopeDisplayMap,
  DEFAULT_PUBLIC_SCOPE_DISPLAY,
  type PublicScopeDisplay,
} from '../../../lib/weeklyReportPublicScopeDisplay';



/* =========================================================
 * 型・定数
 * ======================================================= */

type Suggestion = { id: number; label: string };

interface DynamicFieldItem {
  id: string;
  type: string; // 表示名 = attr_name or 固定名
  value: any;
  label?: string;
  attr_id?: number;
  options?: { value: string; label: string }[];
  divisionContext?: string;
  isRemovable: boolean;
  isRequired?: boolean;
}

type AttrFromMaster = {
  attr_id: number;
  department_id: number;
  team_id: number;
  attr_name: string;
  label?: string;
  attr_required?: number | string;
  input_required?: number | string | boolean;
  attr_type: number | string; // 1:text … 11:select, 12:checkbox, 13:datalist
  multiple_select?: number | string | boolean;
  order_number?: number | string;
  status?: string | number | boolean;
  options?: Array<{ value?: string | number; label?: string; option_id?: number; id?: number }> | null;
};

type Prefill = {
  /** 週報ID（Overwrite選択時の再初期化判定に使用） */
  wr_id?: number | string;
  /** YYYY-MM-DD */
  start_date?: string;
  /** YYYY-MM-DD */
  end_date?: string;
  /** 記入者（名前で渡る想定） */
  create_by?: string;
  /** 選択された課ID配列 */
  departments?: number[];
  /** 単数の課IDも受け取る */
  department?: number;
  /** team_id または チーム名 */
  team?: number | string;
  /** 週報分類の value/id 配列（どちらでも可） */
  report_types?: Array<string | number>;
  /** 表示用: 作成/更新者（ここでは表示しないが保持したい場合に） */
  update_by?: string;
  /** 既存タイトル/本文（ここでは表示しないが保持したい場合に） */
  title?: string;
  body?: string;
  /** 承認-3 */
  approver?: string;
  /** 承認AM */
  approve_am?: string;
  /** 課題メンバー */
  respondents?: string[] | Record<string, any> | string | null;
  respondents_1?: string[] | Record<string, any> | string | null;
  task_members?: any[] | Record<string, any> | string | null;
  members?: any[] | Record<string, any> | string | null;
  taskMembers?: any[] | Record<string, any> | string | null;
  /** 動的属性値（配列 or JSON文字列; 要素は {attr_id, attr_value}） */
  attribute_values?:
    | Array<{ attr_id?: number | string; attr_name?: string; attr_value?: string } | string>
    | string
    | null;

  attributes?: Array<{ attr_id?: number | string; attr_name?: string; attr_value?: string }> | null;
  attributeValues?: Array<{ attr_id?: number | string; attr_name?: string; attr_value?: string }> | null;
  base_attributes?: Array<{ attr_id?: number | string; attr_name?: string; attr_value?: string }> | null;
  baseAttributes?: Array<{ attr_id?: number | string; attr_name?: string; attr_value?: string }> | null;
  weekly_report_base_attributes?: Array<{ attr_id?: number | string; attr_name?: string; attr_value?: string }> | null;

  /** Project を週報分類と同じノリでプレフィル対応（単体/配列の両方を許容） */
  project?: string | number;
  projects?: Array<string | number>;
  shikimuke?: string | number;
  destination?: string | number;
  lot?: string | number;
  powertrain?: string | number;
};

interface Props {
  /** モーダル選択後のレコード（これを UI に反映） */
  prefill?: Prefill;

  /** マスター（SearchArea1st 同等の構造）。未指定でもOK（その場合、動的属性の表示だけスキップ可） */
  master: WeeklyMaster | null;

  /** props */
  enlightSyncEnabled: boolean;
  onFieldsSnapshot?: (fields: any[]) => void;
  onFreeWordsChange?: (freeWords: Record<string, string>) => void;
  onAttributesChange?: (attrs: Array<{ attr_id: number; attr_name: string; attr_value: string }>) => void;

  /** 親に承認AM変更を伝える */
  onApproverAMChange?: (value: string) => void;

  /** 検索項目の「現在 UI に表示されている値」をペイロード用に通知（オプショナル） */
  onSearchPayloadChange?: (payload: SearchSelectionPayload) => void;

  onApprover3Change?: (value: string) => void;
  teamValue?: string | null;
  onTeamChange?: (value: string | null) => void;
  onDateRangeChange?: (range: { from: string; to: string }) => void;

  /** 現在選択されている課の department_id（division の変更反映用; 優先使用） */
  departmentId?: number | null;
  readOnly?: boolean;
  loginId?: string;
}

/** 固定ラベル → CSS クラス名 */
const clsMap: Record<string, string> = {
  チーム: 'team',
  記入者: 'reporter',
  連名者: 'task-members',
  '承認-3': 'approver3',
  承認AM: 'approver-am',
  [CLASSIFICATION_MARKET_TARGET_ATTR_NAME]: 'report-type',
  開始終了日付: 'date-range',
  [FIXED_ATTR_NAME_PROJECT]: 'project',
  [FIXED_ATTR_NAME_POWERTRAIN]: 'powertrain',
  [FIXED_ATTR_NAME_SHIMUKE]: 'destination',
  [FIXED_ATTR_NAME_LOT]: 'lot',
};

/** 真に固定の項目（"属性"ではない） */
const FIXED_BASE_NAMES = new Set<string>([
  'チーム',
  '記入者',
  '連名者',
  '承認-3',
  '承認AM',
  '開始終了日付',
]);

const APPROVER3_DISPLAY_LABEL = '職制';
// 共通フリー欄の旧/新名称をまとめて扱う
const COMMON_FREE_TYPE_NAMES = [
  '自由入力欄',
  '自由入力欄（大）',
  'フリー入力欄',
  'フリー入力欄（大）',
  'フリー',
];

const TASK_MEMBERS_MAX_SELECTED = 10;
const TASK_MEMBERS_MAX_SELECTED_MESSAGE = `最大${TASK_MEMBERS_MAX_SELECTED}名まで選択できます`;

const limitTaskMembers = <T,>(members: T[]): T[] =>
  Array.isArray(members) ? members.slice(0, TASK_MEMBERS_MAX_SELECTED) : [];


/* =========================================================
 * 親に渡す検索選択ペイロード型
 * ======================================================= */

export type SearchSelectionEntry = {
  /** フィールドの種類（例: 'チーム', '記入者', '承認AM', '週報分類', '開始終了日付' など） */
  type: string;
  /** ラベル（UI 表示名） */
  label: string;
  /** 表示上の値（文字列化、複数はカンマ結合） */
  display: string;
  /** UI の内部保持値（型そのまま） */
  raw: any;
  /** 動的属性なら attr_id（固定項目は undefined） */
  attr_id?: number;
  /** 固定項目かどうか */
  isFixed: boolean;
};

export type SearchSelectionPayload = {
  /**
   * 画面に見えている “名前→表示値” のフラットマップ
   * 例: { 'チーム': 'Aチーム', '記入者': '山田太郎', '週報分類': '内報', ... }
   */
  displayMap: Record<string, string>;
  /** 各フィールドの詳細（配列） */
  entries: SearchSelectionEntry[];
  /** 固定項目の構造化データ（必要に応じて親で利用） */
  fixed: {
    team?: { value: string; label: string };
    reporter?: { value: string; label: string };
    approver3?: { value: string; label: string };
    approverAM?: { value: string; label: string };
    dateRange?: { from: string; to: string };
    taskMembers?: string[];
  };
  /**
   * 動的属性（バックエンド保存向け）
   * 既存の onAttributesChange と同じ形もここに含めます（親で取り回しやすいように）
   */
  dynamicAttrs: Array<{ attr_id: number; attr_name: string; attr_value: string }>;
  meta?: {
    teamCleared?: boolean;
    teamClearedByUser?: boolean;
  };
  /** 便利情報（UI でまとめている表示値） */
  extras: {
    reportType?: string;
    shikimuke?: string;
    lot?: string;
    powertrain?: string;
    project?: string;         // Project の表示用文字列
    teamCleared?: boolean;
    teamClearedByUser?: boolean;
  };
};

/* =========================================================
 * ユーティリティ
 * ======================================================= */

// ===============================
// JSXの value に渡す「Option or null」正規化ヘルパ
// （toSelected が未定義エラーになるのを防ぐ）
// ===============================
const toSelected = <T extends { value?: any }>(opt: T | null | undefined): T | null => {
  if (!opt) return null;
  const v = norm((opt as any).value ?? '');
  return v ? opt : null;
};

// options から "ラベル優先" 検索を行うためのインデックスを構築
const buildOptionIndex = (opts: { value: string; label: string }[]) => {
  const byLabel = new Map<string, { value: string; label: string }>();
  const byValue = new Map<string, { value: string; label: string }>();

  for (const o of opts) {
    const ln = norm(o.label);
    const vn = norm(o.value);

    // label -> option
    if (ln && !byLabel.has(ln)) byLabel.set(ln, o);

    // 角括弧付きの別名（[EV] → EV）も label として拾う
    const debracket = ln.replace(/^\[(.+)\]$/, '$1');
    if (debracket !== ln && !byLabel.has(debracket)) byLabel.set(debracket, o);

    // value -> option（数値IDなど）
    if (vn && !byValue.has(vn)) byValue.set(vn, o);
  }
  return { byLabel, byValue };
};

// 候補文字列から、ラベル優先 ダメなら value で解決
const pickOptionPreferLabel = (
  txt: string,
  index: ReturnType<typeof buildOptionIndex>
): { value: string; label: string } | null => {
  // 既存の normalizeForMatch と norm を活かす
  for (const key of normalizeForMatch(txt)) {
    const n = norm(key);
    const byL = index.byLabel.get(n);
    if (byL) return byL;
    const byV = index.byValue.get(n);
    if (byV) return byV;
  }
  return null;
};

const norm = (s: any) => String(s ?? '').normalize('NFKC').trim();

const sameName = (a?: string, b?: string) => norm(a) === norm(b);

const slugify = (s: string) => norm(s).toLowerCase().replace(/[^\w\u3040-\u30ff\u4e00-\u9faf-]+/g, '-');

const PROCESS_PLANT_ATTR_KEY = '工順生産拠点';

const normalizeLayoutAttrName = (name: string) => norm(name).replace(/[\s()（）]/g, '');

const isProcessPlantAttr = (name: string) => normalizeLayoutAttrName(name) === PROCESS_PLANT_ATTR_KEY;

const prioritizeProcessPlantAttr = (names: readonly string[]): string[] => {
  const processPlantNames = names.filter(isProcessPlantAttr);
  if (processPlantNames.length === 0) return [...names];
  return [...processPlantNames, ...names.filter((name) => !isProcessPlantAttr(name))];
};

const toAttrTypeCode = (t: number | string | undefined): number => {
  const s = norm(t);
  const map: Record<string, number> = {
    '1': 1,
    text: 1,
    '2': 2,
    textarea: 2,
    '3': 3,
    int: 3,
    integer: 3,
    '4': 4,
    decimal: 4,
    float: 4,
    '5': 5,
    date: 5,
    '6': 6,
    time: 6,
    '11': 11,
    select: 11,
    dropdown: 11,
    '12': 12,
    checkbox: 12,
    multiselect: 12,
    '13': 13,
    datalist: 13,
    combo: 13,
  };
  if (s in map) return map[s];
  const n = Number(s);
  return Number.isFinite(n) ? n : 1;
};

const toMultiFlag = (v: number | string | boolean | undefined): 0 | 1 => {
  const s = norm(v).toLowerCase();
  return s === '1' || s === 'true' || s === 't' || s === 'yes' || s === 'y' || s === 'on' ? 1 : 0;
};

/* ---------- option 解決（value/label 両対応） ---------- */
type SimpleOption = { value: string; label: string };

const findOptionByValueOrLabel = (raw: any, opts: SimpleOption[]): SimpleOption | null => {
  const s = norm(raw);
  if (!s) return null;
  return opts.find((o) => sameName(o.value, s) || sameName(o.label, s)) ?? null;
};

const isSelectType = (t?: number | string) => [11, 12, 13].includes(toAttrTypeCode(t));

const isFreeWordType = (t?: number | string) => !isSelectType(t);

const parseTeamFiscalYear = (raw: unknown): number | null => {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw !== 'string') return null;

  const s = raw.trim().toUpperCase();
  if (!s) return null;

  const fyMatch = s.match(/^FY(\d{2}|\d{4})$/);
  if (fyMatch) {
    const n = Number(fyMatch[1]);
    if (!Number.isFinite(n)) return null;
    return fyMatch[1].length === 2 ? 2000 + n : n;
  }

  if (/^\d{4}$/.test(s)) {
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }

  return null;
};

const isPastTeam = (teamFiscalYear: unknown): boolean => {
  const fy = parseTeamFiscalYear(teamFiscalYear);
  return fy != null;
};

const getTeamEndFiscalYear = (team: any): unknown =>
  team?.end_fiscal_year ?? team?.team_fiscal_year;

const toOption = (x: any): { value: string; label: string; groupLabel?: string } => {
  if (typeof x === 'string' || typeof x === 'number') {
    const s = norm(x);
    return { value: s, label: s };
  }
  const groupLabel = norm(
    x?.groupLabel ??
      x?.team_classification_name ??
      x?.team_class_name ??
      x?.team_classification ??
      x?.class_name ??
      x?.classification_name ??
      ''
  );
  const value = norm(x?.value ?? x?.label ?? x?.name ?? x?.text ?? '');
  const label = norm(x?.label ?? x?.name ?? x?.text ?? x?.value ?? value);
  return groupLabel ? { value, label, groupLabel } : { value, label };
};

const normalizeOptions = (
  arr: any[] | undefined | null
): { value: string; label: string; groupLabel?: string }[] =>
  (arr ?? [])
    // status='N' や false の候補は除外
    .filter((o: any) => String((o as any)?.status ?? '').toUpperCase() !== 'N' && (o as any)?.status !== false)
    .map(toOption);

const uniqueOptions = (opts: { value: string; label: string; groupLabel?: string }[]) => {
  const seen = new Set<string>();
  return (opts ?? []).filter((o) => {
    const k = `${o.value}__${o.label}__${norm(o.groupLabel ?? '')}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

// ===== options の「共通」を先頭に寄せる（表示順だけ変更） =====
const moveCommonToTop = (opts: { value: string; label: string }[]) => {
  const isCommon = (o: { value: string; label: string }) =>
    norm(o.label) === '共通' || norm(o.value) === '共通';

  const head = (opts ?? []).filter(isCommon);
  const tail = (opts ?? []).filter((o) => !isCommon(o));
  return [...head, ...tail];
};

// ===== 「共通」を候補がない場合に入れるための定数 =====
const COMMON_LABEL = '共通';
const COMMON_OPTION = { value: COMMON_LABEL, label: COMMON_LABEL };

// 「共通」が候補に無ければ追加し、必ず先頭に来るようにする
const ensureCommonOptionAtTop = (opts: { value: string; label: string }[]) => {
  const hasCommon = (opts ?? []).some(
    (o) => norm(o.label) === COMMON_LABEL || norm(o.value) === COMMON_LABEL
  );

  const merged = hasCommon ? (opts ?? []) : [COMMON_OPTION, ...(opts ?? [])];

  // ついでに重複も落としてから並び替え
  return moveCommonToTop(uniqueOptions(merged));
};

const toSuggestions = (opts: { value: string; label: string }[]): Suggestion[] =>
  opts.map((o, i) => {
    const n = Number(o.value);
    return { id: Number.isFinite(n) ? n : i + 1, label: o.label };
  });

const uniqueNames = (arr: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of arr) {
    const k = String(s ?? '');
    if (!seen.has(k)) {
      seen.add(k);
      out.push(k);
    }
  }
  return out;
};

// 連名者など「人名」向け：空白ゆらぎも潰してユニーク化
const normNameKey = (s: any) => norm(s).replace(/\s+/g, ' ').trim();

const uniqueNormNames = (arr: any[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  (arr ?? []).forEach((x) => {
    const v = normNameKey(x);
    if (!v) return;
    if (seen.has(v)) return;
    seen.add(v);
    out.push(v);
  });
  return out;
};

const dedupeByNameKey = (opts: { value: string; label: string }[]) => {
  const seen = new Set<string>();
  return (opts ?? []).filter((o) => {
    const key = coAuthorNameKey(o?.label ?? o?.value ?? '');
    if (!key) return false;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};


// ===== 人名候補の重複排除（label重複 + value違い問題の対策） =====
const isEmailLike = (s: any) => /\S+@\S+\.\S+/.test(String(s ?? ''));

const dedupePersonOptions = (opts: { value: string; label: string }[]) => {
  const keyOfLabel = (o: { value: string; label: string }) => normNameKey(o.label);

  // labelKey -> options[]
  const groups = new Map<string, { value: string; label: string }[]>();
  const order: string[] = [];

  (opts ?? []).forEach((o) => {
    const labelKey = keyOfLabel(o);
    if (!labelKey) return;

    if (!groups.has(labelKey)) {
      groups.set(labelKey, []);
      order.push(labelKey);
    }
    groups.get(labelKey)!.push(o);
  });

  const out: { value: string; label: string }[] = [];

  order.forEach((labelKey) => {
    const list = groups.get(labelKey) ?? [];
    if (list.length === 0) return;

    // 「ダミー」= value と label が同じ（prefill/respondents由来に多い）
    const isDummy = (o: { value: string; label: string }) =>
      norm(o.value) === norm(o.label);

    // 「実体」= value != label（メール/IDなど） or emailっぽい
    const isReal = (o: { value: string; label: string }) =>
      !isDummy(o) || isEmailLike(o.value);

    const hasReal = list.some(isReal);

    // 同じlabelに実体があるなら、ダミーは捨てる
    const keep = hasReal ? list.filter(isReal) : [list[0]];

    // 最終的に value 重複も落とす（安全）
    const seenV = new Set<string>();
    keep.forEach((o) => {
      const v = norm(o.value);
      if (!v) return;
      if (seenV.has(v)) return;
      seenV.add(v);
      out.push(o);
    });
  });

  return out;
};

const isNumericLike = (s: any) => /^[+-]?\d+$/.test(norm(s));

/**
 * select(複数)の選択値を options に寄せて正規化する
 * - options に存在しない「数値IDっぽい値」は捨てる（91/164問題）
 */
const sanitizeSelectArrayByOptions = (
  raw: any,
  opts: { value: string; label: string }[],
  allowUnknown = true
): Array<{ value: string; label: string }> => {
  const arr: any[] = Array.isArray(raw) ? raw : [];

  const optList = uniqueOptions((opts ?? []).map(toOption));
  const index = buildOptionIndex(optList);

  const out: Array<{ value: string; label: string }> = [];
  const seen = new Set<string>();

  arr.forEach((x) => {
    const v0 =
      typeof x === 'string' || typeof x === 'number'
        ? norm(x)
        : norm(x?.value ?? x?.label ?? '');
    const l0 =
      typeof x === 'string' || typeof x === 'number'
        ? norm(x)
        : norm(x?.label ?? x?.value ?? '');

    const hit = pickOptionPreferLabel(v0, index) || pickOptionPreferLabel(l0, index);

    // マスタ選択項目では、過去の分割処理で生じた候補外の値を復元しない。
    if (!hit && !allowUnknown) return;

    // ★ options に無い「数値だけ」は捨てる（= IDの迷い込み対策）
    if (!hit && (isNumericLike(v0) || isNumericLike(l0))) return;

    const v = norm(hit?.value ?? v0 ?? l0);
    const l = norm(hit?.label ?? l0 ?? v0 ?? v);

    if (!v) return;
    if (seen.has(v)) return;
    seen.add(v);

    out.push({ value: v, label: l || v });
  });

  return out;
};

/**
 * select(単一)を options に寄せて正規化する
 * - options に存在しない「数値IDっぽい値」は空にする（91/164問題）
 * 戻り値:
 *  - ''（未選択）
 *  - {value,label}
 */
const sanitizeSingleSelectByOptions = (
  raw: any,
  opts: { value: string; label: string }[]
): '' | { value: string; label: string } => {
  const baseOpts = uniqueOptions((opts ?? []).map(toOption));
  const index = buildOptionIndex(baseOpts);

  const curVal =
    typeof raw === 'object' && raw !== null ? norm((raw as any).value ?? (raw as any).label ?? '') : norm(raw);
  const curLbl =
    typeof raw === 'object' && raw !== null ? norm((raw as any).label ?? (raw as any).value ?? '') : '';

  const key = curVal || curLbl;
  if (!key) return '';

  const hit = pickOptionPreferLabel(key, index) || pickOptionPreferLabel(curLbl || curVal, index);

  // options に無い数値IDは空にする
  if (!hit && isNumericLike(key)) return '';

  if (!hit) {
    // 数値ではない未知値は “自由入力扱い” として保持（必要ならここを '' に変えてOK）
    const k = norm(key);
    return k ? { value: k, label: k } : '';
  }

  return { value: hit.value, label: hit.label };
};


/** 表示優先で値化（label → value → name） */
const pickValueLike = (v: any): string => {
  if (v == null) return '';
  if (Array.isArray(v)) {
    return v
      .map((x) => (typeof x === 'object' ? String(x.label ?? x.value ?? x.name ?? '') : String(x)))
      .map(norm)
      .filter(Boolean)
      .join(',');
  }
  if (typeof v === 'object') {
    return norm((v as any).label ?? (v as any).value ?? (v as any).name ?? JSON.stringify(v));
  }
  return norm(v);
};

  /** バケットから候補を抽出（仕向け/ロット/パワートレインは label を value として扱う） */
  const optionsFromBuckets = (
    master: WeeklyMaster | null,
    attrName?: string,
    attrId?: number,
    departmentId?: number,
    teamId?: number
  ): { value: string; label: string }[] => {
    const buckets = (master as any)?.attributeBuckets as Array<{ name: string; items: any[] }> | undefined;
    if (!buckets) return [];

    const preferLabelAsValue =
      attrName === FIXED_ATTR_NAME_SHIMUKE || attrName === FIXED_ATTR_NAME_LOT || attrName === FIXED_ATTR_NAME_POWERTRAIN;

    const make = (it: any, idx: number) => {
      const label = norm(it?.label ?? it?.name ?? it?.text ?? it?.value ?? `item-${idx + 1}`);
      const value = preferLabelAsValue ? label : (it?.option_id ?? it?.id ?? it?.value ?? label) + '';
      return { value: norm(value), label };
    };

    const filterByScope = (items: any[]) => {
      const tn = Number(teamId) || 0;
      let dn = Number(departmentId) || 0;

      // [FIX-1] Project も週報分類と同じくチーム厳格スコープで扱う
      const strictTeamScope = attrName === CLASSIFICATION_MARKET_TARGET_ATTR_NAME || attrName === FIXED_ATTR_NAME_PROJECT;

      // 1) チーム固有
      const teamItems = (items ?? []).filter((it: any) => Number(it?.team_id ?? it?.teamId) === tn && tn > 0);
      if (teamItems.length) return teamItems;

      if (tn > 0) {
        const teams: any[] = Array.isArray((master as any)?.teams) ? (master as any).teams : [];
        const hitTeam = teams.find((t: any) => Number(t?.team_id ?? t?.id) === tn);
        const teamDeptId = Number(hitTeam?.department_id) || 0;
        if (teamDeptId) dn = teamDeptId;

        // 2) 課スコープ
        const deptItems = (items ?? []).filter(
          (it: any) => Number(it?.team_id ?? it?.teamId) === 0 && Number(it?.department_id ?? it?.departmentId) === dn && dn > 0
        );
        if (deptItems.length) return deptItems;

        // 3) 全社スコープ
        const companyItems = (items ?? []).filter(
          (it: any) => Number(it?.team_id ?? it?.teamId) === 0 && Number(it?.department_id ?? it?.departmentId) === 0
        );
        if (companyItems.length) return companyItems;

        // 4) 厳格スコープ対象は空配列で返す（他チームの候補を引きずらない）
        if (strictTeamScope) return [];

        return items ?? [];
      }

      // チーム未決定時
      const deptItemsNoTeam = (items ?? []).filter(
        (it: any) => Number(it?.team_id ?? it?.teamId) === 0 && Number(it?.department_id ?? it?.departmentId) === dn && dn > 0
      );
      if (deptItemsNoTeam.length) return deptItemsNoTeam;

      const companyItemsNoTeam = (items ?? []).filter(
        (it: any) => Number(it?.team_id ?? it?.teamId) === 0 && Number(it?.department_id ?? it?.departmentId) === 0
      );
      if (companyItemsNoTeam.length) return companyItemsNoTeam;

      return items ?? [];
    };

    const toOptions = (items: any[]) => {
      const seen = new Set<string>();
      const out: { value: string; label: string }[] = [];
      const base = filterByScope(items)
        // attributeBuckets 由来の項目で status='N' や false は除外
        .filter((it: any) => String((it as any)?.status ?? '').toUpperCase() !== 'N' && (it as any)?.status !== false);
      base.forEach((it, i) => {
        const o = make(it, i);
        const k = `${o.value}__${o.label}`;
        if (!o.label || seen.has(k)) return;
        seen.add(k);
        out.push(o);
      });
      return out;
    };

    if (typeof attrId === 'number') {
      const items = buckets.flatMap((b) => (b.items ?? []).filter((it: any) => Number(it?.attr_id ?? it?.attrId) === Number(attrId)));
      const out = toOptions(items);
      if (out.length) return out;
    }

    if (attrName) {
      const bucket = buckets.find((b) => norm(b?.name) === norm(attrName));
      if (bucket) return toOptions(bucket.items ?? []);
    }

    return [];
  };

const sanitizeIntStrict = (raw: any) => {
  const s0 = String(raw ?? '');
  if (!s0) return '';
  let s = s0.replace(/[^\d-]/g, '');
  const neg = s.startsWith('-');
  s = s.replace(/-/g, '');
  return (neg ? '-' : '') + s;
};

//「空白に挟まれている場合のみ」区切る
const SPLIT_MULTI_SELECT = /\s*[,、，]\s*|\s+[\/／]\s+/;

const prefillHasAttrId = (raw: Prefill['attribute_values'], attrId: number): boolean => {
  if (!raw) return false;

  let arr: any[] = [];
  if (Array.isArray(raw)) {
    arr = raw;
  } else if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) arr = parsed;
    } catch {
      return false;
    }
  } else {
    return false;
  }

  return arr.some((item) => {
    const idRaw =
      (item as any)?.attr_id ??
      (item as any)?.attri_id ?? // typo
      (item as any)?.attribute_id ??
      (item as any)?.id;
    return Number(idRaw) === Number(attrId);
  });
};

const normalizePrefillAttributeValueArray = (raw: any) => {
  if (!raw) {
    return [] as Array<{ attr_id?: number | string; attr_name?: string; attr_value?: string }>;
  }

  let arr: any[] = [];

  if (Array.isArray(raw)) {
    arr = raw;
  } else if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        arr = parsed;
      }
    } catch {
      arr = [];
    }
  }

  return arr
    .filter((item) => item && typeof item === 'object')
    .map((item: any) => ({
      attr_id: item.attr_id ?? item.attrId ?? item.attri_id ?? item.attribute_id ?? item.id,
      attr_name:
        item.attr_name ??
        item.attrName ??
        item.attribute_name ??
        item.attributeName ??
        item.name ??
        item.label,
      attr_value: item.attr_value ?? item.attrValue ?? item.value ?? item.text ?? '',
    }))
    .filter((item) => {
      const hasId = item.attr_id != null && String(item.attr_id).trim() !== '';
      const hasName = item.attr_name != null && String(item.attr_name).trim() !== '';
      const hasValue = item.attr_value != null && String(item.attr_value).trim() !== '';
      return (hasId || hasName) && hasValue;
    });
};

const getPrefillAttributeValues = (prefill?: Prefill) => {
  const candidates = [
    prefill?.attribute_values,
    prefill?.attributes,
    prefill?.attributeValues,
    prefill?.base_attributes,
    prefill?.baseAttributes,
    prefill?.weekly_report_base_attributes,
  ];

  for (const raw of candidates) {
    const normalized = normalizePrefillAttributeValueArray(raw);

    if (normalized.length > 0) {
      return normalized;
    }
  }

  return [] as Array<{ attr_id?: number | string; attr_name?: string; attr_value?: string }>;
};

/** 入力値を一切省かずに文字列化（改行維持、句読点や語尾もそのまま） */
const keepRawToken = (v: any): string => {
  if (v == null) return '';

  // よくある形（string/number/object）を安全に文字列化
  let s = '';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
    s = String(v);
  } else if (typeof v === 'object') {
    const picked =
      (v as any).attr_value ??
      (v as any).value ??
      (v as any).label ??
      (v as any).text ??
      (v as any).name;
    if (picked != null) {
      s = String(picked);
    } else {
      try {
        s = JSON.stringify(v);
      } catch {
        s = String(v);
      }
    }
  } else {
    s = String(v);
  }

  // 改行コードだけは統一（表示崩れ防止）。それ以外は削らない。
  return s.replace(/\r\n?/g, '\n');
};

/** 文末の不要表現や句読点を除去（ES5 ターゲットでもOK） */
const cleanupToken = (s: string) =>
  norm(s)
    .replace(/です。?$/, '')
    .replace(/[。．.]+$/, '')
    .trim();

/** 角カッコを剥がした比較キー（[FR] -> FR）も試す */
const normalizeForMatch = (s: string) => {
  const t = cleanupToken(s);
  return [t, t.replace(/^\[(.+)\]$/, '$1')];
};

/** 先頭に空要素を追加（未選択サポート） */
const withEmpty = (opts: { value: string; label: string }[], label: string) => {
  return [{ value: '', label: `-- ${label} --` }, ...opts];
};

/** options に「現在値」が無いとき、表示できるように options に追加する */
const ensureOptionExistsInOptions = (
  opts: { value: string; label: string }[],
  selected: '' | { value: string; label: string } | null | undefined
): { value: string; label: string }[] => {

  if (selected == null || selected === '') return opts ?? [];

  const v = norm(selected.value);
  const l = norm(selected.label) || v;
  if (!v) return opts ?? [];

  const base = uniqueOptions((opts ?? []).map(toOption));
  const exists = base.some((o) => norm(o.value) === v);

  // 既にあればそのまま
  if (exists) return base;

  // 無ければ注入（これで CommonSelect が先頭候補に勝手に寄らない）
  return uniqueOptions([...base, { value: v, label: l }]);
};

// ===============================
// Top-level & memoized version
// ===============================
type CheckboxDropdownSelectProps = {
  id: string;
  placeholder?: string;
  options: { value: string; label: string }[];
  value: Array<{ value: string; label: string }>;
  onChange: (next: Array<{ value: string; label: string }>) => void;
  enableSearch?: boolean;
  searchPlaceholder?: string;
  allowCustomValue?: boolean;

  /**
   * 「すべて（表示中）」のトグルを表示するか
   * - 連名者: true
   * - それ以外: false（SearchArea1st と同じ挙動に合わせる）
   */
  showSelectAll?: boolean;
  optionDescriptions?: Record<string, PublicScopeDisplay>;
  defaultOptionDescription?: PublicScopeDisplay;
  summaryVisibleCount?: number;
  selectAllLabel?: string; // default: 'すべて'
};

// ===============================
// Top-level & memoized version
// ===============================
const CheckboxDropdownSelect: React.FC<CheckboxDropdownSelectProps> = React.memo(
  ({
    id,
    placeholder,
    options,
    value,
    onChange,
    enableSearch = true,
    searchPlaceholder = 'キーワードで絞り込み',
    allowCustomValue = false,
    showSelectAll = false,
    optionDescriptions,
    defaultOptionDescription,
    summaryVisibleCount = 3,
    selectAllLabel = 'すべて',
  }) => {
    const rootRef = useRef<HTMLDivElement | null>(null);
    const [open, setOpen] = useState(false);
    const [keyword, setKeyword] = useState('');

    // 親が追いつくまでの巻き戻し防止（内部で pending を持つ）
    const pendingSigRef = useRef<string>('');

    // 外クリックで閉じる（中をクリックしても閉じない）
    useEffect(() => {
      const h = (e: MouseEvent | TouchEvent) => {
        const el = e.target as Node | null;
        if (rootRef.current && el && !rootRef.current.contains(el)) setOpen(false);
      };
      document.addEventListener('mousedown', h);
      document.addEventListener('touchstart', h);
      return () => {
        document.removeEventListener('mousedown', h);
        document.removeEventListener('touchstart', h);
      };
    }, []);

    useEffect(() => {
      if (!open) setKeyword('');
    }, [open]);

    const tokens = useMemo(
      () =>
        String(keyword ?? '')
          .normalize('NFKC')
          .trim()
          .split(/\s+/)
          .filter(Boolean),
      [keyword]
    );

    // value を options に寄せて正規化（value/label どちらで来ても一致できるようにする）
    const canonicalizeSelected = useCallback(
      (raw: any): Array<{ value: string; label: string }> => {
        const picked = Array.isArray(raw)
          ? raw.map((v: any) => ({ value: norm(v?.value), label: norm(v?.label) }))
          : [];

        const optList = uniqueOptions((options ?? []).map(toOption));
        const byValue = new Map<string, { value: string; label: string }>();
        const byLabel = new Map<string, { value: string; label: string }>();

        optList.forEach((o) => {
          byValue.set(norm(o.value), o);
          const l = norm(o.label);
          if (l && !byLabel.has(l)) byLabel.set(l, o);
        });

        const out: Array<{ value: string; label: string }> = [];
        const seen = new Set<string>();

        picked.forEach((s) => {
          const v = norm(s.value);
          const l = norm(s.label);

          const hit =
            byValue.get(v) ||
            byValue.get(l) || // value に label が入ってくるケース救済
            byLabel.get(l) ||
            byLabel.get(v); // label に value が入ってくるケース救済

          // カスタム入力を許可していない項目では、候補にない値を選択数へ含めない。
          if (!hit && !allowCustomValue) return;

          const next = hit
            ? { value: hit.value, label: hit.label }
            : { value: v || l, label: l || v || (v || l) };

          if (!next.value) return;
          if (seen.has(next.value)) return;
          seen.add(next.value);
          out.push(next);
        });

        return out;
      },
      [options, allowCustomValue]
    );

    const sigOf = useCallback((arr: Array<{ value: string; label: string }>) => {
      const keys = arr
        .map((x) => norm(x.value))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, 'ja'));
      return keys.join('|');
    }, []);

    // UIは local を正にする（クリック直後に親が遅れてもチェックが外れない）
    const [localSelected, setLocalSelected] = useState<Array<{ value: string; label: string }>>(() =>
      canonicalizeSelected(value)
    );

    // props(value/options) 変化時：pending が解消するまで local を巻き戻さない
    useEffect(() => {
      const next = canonicalizeSelected(value);
      const nextSig = sigOf(next);

      if (pendingSigRef.current) {
        if (pendingSigRef.current !== nextSig) {
          // 親がまだ古い（または別effectで巻き戻された）→ local を触らない
          return;
        }
        // 親が追いついた → pending解除
        pendingSigRef.current = '';
      }

      const curSig = sigOf(localSelected);
      if (curSig !== nextSig) {
        setLocalSelected(next);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, options, allowCustomValue]);

    const baseOptions = useMemo(() => uniqueOptions(options ?? []), [options]);
    const baseValueSet = useMemo(() => new Set(baseOptions.map((o) => norm(o.value))), [baseOptions]);

    const selectedCustom = useMemo(
      () =>
        allowCustomValue
          ? uniqueOptions((localSelected ?? []).filter((s) => !baseValueSet.has(norm(s.value))))
          : [],
      [localSelected, baseValueSet, allowCustomValue]
    );

    const optionsForMap = useMemo(
      () => uniqueOptions([...(baseOptions ?? []), ...(selectedCustom ?? [])]),
      [baseOptions, selectedCustom]
    );

    const filtered = useMemo(() => {
      if (!enableSearch || tokens.length === 0) return baseOptions;
      return baseOptions.filter((o) => {
        const lv = String(o.label).normalize('NFKC').trim();
        const vv = String(o.value).normalize('NFKC').trim();
        return tokens.every((t) => lv.includes(t) || vv.includes(t));
      });
    }, [baseOptions, tokens, enableSearch]);

    const customCandidate = useMemo(() => {
      if (!allowCustomValue) return null;
      const raw = norm(keyword);
      if (!raw) return null;
      const exists = optionsForMap.some((o) => norm(o.label) === raw || norm(o.value) === raw);
      if (exists) return null;
      return { value: raw, label: raw };
    }, [allowCustomValue, optionsForMap, keyword]);

    // 検索中は「表示中」のみ対象（showSelectAll の対象もこれ）
    const target = keyword ? filtered : baseOptions;

    const selectedSet = useMemo(() => new Set(localSelected.map((v) => v.value)), [localSelected]);

    const apply = useCallback(
      (vals: string[]) => {
        const map = new Map(optionsForMap.map((o) => [o.value, o.label]));

        const next = vals.map((v) => ({
          value: v,
          label: map.get(v) ?? v,
        }));

        // local を即更新（UIの真実）
        setLocalSelected(next);

        // 親が追いつくまで巻き戻しブロック
        pendingSigRef.current = sigOf(next);

        onChange(next);
      },
      [optionsForMap, onChange, sigOf]
    );

    const toggleOne = useCallback(
      (v: string) => {
        const s = new Set(selectedSet);
        const wasSelected = s.has(v);
        wasSelected ? s.delete(v) : s.add(v);
        apply(Array.from(s));
        if (allowCustomValue && !baseValueSet.has(norm(v))) {
          setKeyword('');
        }
      },
      [selectedSet, apply, allowCustomValue, baseValueSet]
    );

    const allSelectedForTarget =
      showSelectAll && target.length > 0 && target.every((o) => selectedSet.has(o.value));
    const someSelectedForTarget =
      showSelectAll && !allSelectedForTarget && target.some((o) => selectedSet.has(o.value));
    const canShowSelectAll = showSelectAll && (target.length > 0 || (allowCustomValue && keyword));

    const toggleAll: React.ChangeEventHandler<HTMLInputElement> = useCallback(
        (e) => {
          e.stopPropagation();
          if (!showSelectAll) return;
          if (target.length === 0) {
            if (allowCustomValue && norm(keyword)) setKeyword('');
            return;
          }

          if (allSelectedForTarget) {
            // 表示中を全部解除（対象外の選択は保持）
            const targetSet = new Set(target.map((o) => o.value));
            const keep = Array.from(selectedSet).filter(
              (v) => !(targetSet.has(v) && baseValueSet.has(norm(v)))
            );
            apply(keep);
            if (allowCustomValue && norm(keyword)) setKeyword('');
            return;
          }

        // 表示中を全部追加（既存も保持）
        const add = new Set<string>(Array.from(selectedSet));
        target.forEach((o) => add.add(o.value));
        apply(Array.from(add));
      },
      [showSelectAll, allSelectedForTarget, selectedSet, target, apply, baseValueSet, allowCustomValue, keyword]
    );

    const summary = useMemo(() => {
      const size = selectedSet.size;
      if (!size) return { text: placeholder ?? '', fullText: '' };

      const optionValueSet = new Set((options ?? []).map((o) => norm(o.value)));
      const hasExtraSelected = (localSelected ?? []).some((s) => !optionValueSet.has(norm(s.value)));

      if (size === options.length && !hasExtraSelected) {
        // 「すべて」表記は showSelectAll=true のときだけ（要件に合わせる）
        if (showSelectAll) {
          return {
            text: selectAllLabel,
            fullText: options.map((o) => o.label).join('、'),
          };
        }

        const labelsAll = options.map((o) => o.label);
        return {
          text:
            labelsAll.length <= summaryVisibleCount
              ? labelsAll.join('、')
              : `${labelsAll.slice(0, summaryVisibleCount).join('、')}、…`,
          fullText: labelsAll.join('、'),
        };
      }

      const labelsFromOptions = options.filter((o) => selectedSet.has(o.value)).map((o) => o.label);
      const labelsFromCustom = (localSelected ?? [])
        .filter((s) => !optionValueSet.has(norm(s.value)))
        .map((s) => s.label || s.value)
        .filter(Boolean);
      const labels = [...labelsFromOptions, ...labelsFromCustom];
      return {
        text:
          labels.length <= summaryVisibleCount
            ? labels.join('、')
            : `${labels.slice(0, summaryVisibleCount).join('、')}、…`,
        fullText: labels.join('、'),
      };
    }, [
      selectedSet,
      options,
      placeholder,
      showSelectAll,
      selectAllLabel,
      localSelected,
      summaryVisibleCount,
    ]);

    const addFromKeyword = useCallback(() => {
      if (!allowCustomValue) return;
      const raw = norm(keyword);
      if (!raw) return;

      const hit = optionsForMap.find((o) => norm(o.label) === raw || norm(o.value) === raw);
      const nextValue = hit ? hit.value : raw;

      if (selectedSet.has(nextValue)) {
        setKeyword('');
        return;
      }

      const next = new Set(selectedSet);
      next.add(nextValue);
      apply(Array.from(next));
      setKeyword('');
    }, [allowCustomValue, optionsForMap, apply, keyword, selectedSet]);

    return (
      <div ref={rootRef} className="cds" data-open={open ? 'true' : 'false'}>
        <button
          type="button"
          className="cds__control"
          title={summary.fullText || undefined}
          onClick={() => setOpen((v) => !v)}
        >
          <span className={`cds__placeholder ${summary.text ? 'is-filled' : ''}`}>
            {summary.text || placeholder || ''}
          </span>
          <span className="cds__arrow" aria-hidden="true" />
        </button>

        {open && (
          <div className="cds__menu" role="listbox" aria-multiselectable="true">
            {enableSearch && (
              <div className="cds__search">
                <input
                  type="text"
                  aria-label="filter"
                  placeholder={searchPlaceholder}
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && allowCustomValue) {
                      e.preventDefault();
                      addFromKeyword();
                      return;
                    }
                    if (e.key === 'Escape') {
                      setKeyword('');
                      (e.currentTarget as HTMLInputElement).blur();
                    }
                  }}
                  className="cds__searchInput"
                />
              </div>
            )}

            <div className="cds__scroll">
              {canShowSelectAll && (
                <label className="cds__option cds__option--all">
                  <input
                    type="checkbox"
                    className="cds__checkbox"
                    checked={allSelectedForTarget}
                    ref={(el) => {
                      if (el) el.indeterminate = !!someSelectedForTarget;
                    }}
                    onChange={toggleAll}
                  />
                  <span>
                    {selectAllLabel}
                    {keyword ? '（表示中）' : ''}
                  </span>
                </label>
              )}

              {customCandidate && (
                <label className="cds__option" aria-selected={selectedSet.has(customCandidate.value)}>
                  <input
                    type="checkbox"
                    className="cds__checkbox"
                    checked={selectedSet.has(customCandidate.value)}
                    onChange={() => toggleOne(customCandidate.value)}
                  />
                  <span>{customCandidate.label}</span>
                </label>
              )}


              {(filtered.length ? filtered : []).map((o) => {
                const checked = selectedSet.has(o.value);
                const description =
                  optionDescriptions?.[String(o.value)] ?? defaultOptionDescription;
                return (
                  <label
                    key={`${id}__${o.value}`}
                    className="cds__option"
                    aria-selected={checked}
                    title={description?.tooltipText}
                  >
                    <input
                      type="checkbox"
                      className="cds__checkbox"
                      checked={checked}
                      onChange={() => toggleOne(o.value)}
                    />
                    <span className="cds__optionText">
                      <span className="cds__optionLabel">{o.label}</span>
                      {description?.summaryText && (
                        <span className="cds__optionScope">{description.summaryText}</span>
                      )}
                    </span>
                  </label>
                );
              })}

              {filtered.length === 0 && !customCandidate && (
                <div className="cds__nohit" role="note">
                  該当する候補がありません
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);
CheckboxDropdownSelect.displayName = 'CheckboxDropdownSelect';

/* =========================================================
 * メイン
 * ======================================================= */

const OverwriteInputSearchArea: React.FC<Props> = ({
  prefill,
  master,
  enlightSyncEnabled,
  onFieldsSnapshot,
  onFreeWordsChange,
  onAttributesChange,
  onApproverAMChange,
  onSearchPayloadChange,
  onApprover3Change,
  teamValue,
  onTeamChange,
  onDateRangeChange,
  departmentId,
  readOnly,
  loginId,
}) => {
  const { palette } = useTheme();
  const isDark = palette.mode === 'dark';
  const reportTypeScopeDisplayByOptionId = useMemo(
    () =>
      buildWeeklyReportPublicScopeDisplayMap({
        rows: master?.wrPublicSettings ?? [],
        departments: master?.departments ?? [],
        teams: master?.teams ?? [],
        maxVisible: 3,
      }),
    [master?.wrPublicSettings, master?.departments, master?.teams]
  );
  const datalistInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const SUPPORTS_DATALIST_PICKER =
    typeof window !== 'undefined' &&
    typeof (HTMLInputElement.prototype as any).showPicker === 'function';

  const SyncIcon = enlightSyncEnabled ? (
    <CheckCircleIcon
      sx={{ fontSize: 16, verticalAlign: 'middle' }}
      color="success"
    />
  ) : (
    <CheckCircleOutlineIcon
      sx={{ fontSize: 16, verticalAlign: 'middle' }}
      color="disabled"
    />
  );

  /* ---------- 親コールバックは ref に退避（無限ループ防止） ---------- */
  const fieldsSnapshotRef = useRef<Props['onFieldsSnapshot'] | null>(null);
  const freeWordsChangeRef = useRef<Props['onFreeWordsChange'] | null>(null);
  const attributesChangeRef = useRef<Props['onAttributesChange'] | null>(null);

  // ユーザーが手で触ったフィールドは attribute_values で再上書きしない（空欄も尊重）
  const touchedTypesRef = useRef<Set<string>>(new Set());

  const markFieldTouched = useCallback((type: string) => {
    touchedTypesRef.current.add(type);
  }, []);

  const resetTouchedTypes = useCallback(() => {
    touchedTypesRef.current.clear();
  }, []);

  // ユーザーがチームを一度でも変更したか（trueなら以後プレフィルで上書きしない）
  const userTouchedTeamRef = useRef(false);
  const [showPastTeams, setShowPastTeams] = useState(false);
  // ユーザーが「記入者」を一度でも変更したか（trueなら以後プレフィルや候補更新で上書きしない）
  const userTouchedReporterRef = useRef(false);

  useEffect(() => {
    fieldsSnapshotRef.current = onFieldsSnapshot ?? null;
  }, [onFieldsSnapshot]);
  useEffect(() => {
    freeWordsChangeRef.current = onFreeWordsChange ?? null;
  }, [onFreeWordsChange]);
  useEffect(() => {
    attributesChangeRef.current = onAttributesChange ?? null;
  }, [onAttributesChange]);

  /* ---------- 実行時パッチ：transform: blur(...) → filter: blur(...) ---------- */
  useEffect(() => {
    const tryPatchStyleSheet = (sheet: CSSStyleSheet) => {
      let rules: CSSRuleList | null = null;
      try {
        rules = sheet.cssRules;
      } catch {
        return;
      }
      if (!rules) return;

      for (let i = 0; i < rules.length; i++) {
        const rule = rules[i] as any;

        if (rule.type === (window.CSSRule?.STYLE_RULE ?? 1)) {
          const style = (rule as CSSStyleRule).style;
          const t = style.getPropertyValue('transform');
          if (t && /blur\(/i.test(t)) {
            const m = t.match(/blur\s*\(\s*[^)]+\)/i);
            if (m) {
              style.removeProperty('transform');
              style.setProperty('filter', m[0]);
            }
          }
        }

        const KEYFRAMES_RULE = (window.CSSRule as any)?.KEYFRAMES_RULE ?? 7;
        if (rule.type === KEYFRAMES_RULE) {
          const kf = rule as CSSKeyframesRule;
          const frames = kf.cssRules || [];
          for (let j = 0; j < frames.length; j++) {
            const frame = frames[j] as any;
            if (frame.type === (window.CSSRule?.KEYFRAME_RULE ?? 8)) {
              const style = (frame as CSSKeyframeRule).style;
              const t = style.getPropertyValue('transform');
              if (t && /blur\(/i.test(t)) {
                const m = t.match(/blur\s*\(\s*[^)]+\)/i);
                if (m) {
                  style.removeProperty('transform');
                  style.setProperty('filter', m[0]);
                }
              }
            }
          }
        }
      }
    };

    try {
      Array.from(document.styleSheets).forEach((sheet) => {
        try {
          const cssRules = (sheet as CSSStyleSheet).cssRules;
          if (cssRules !== undefined) {
            tryPatchStyleSheet(sheet as CSSStyleSheet);
          }
        } catch {
          /* no-op: クロスオリジンなどでアクセス不可 */
        }
      });
    } catch {
      /* no-op */
    }
  }, []); // 初回だけ

  /* ---------- 上位固定フィールド（ローカル管理） ---------- */
  const [division] = useState<string>(''); // 部署セレクトはここでは出さない
  const [team, setTeam] = useState<string>(() => String(teamValue ?? '').trim()); // team_id（文字列）
  const [reporter, setReporter] = useState<string>(''); // 記入者（option.value）
  const [reportType, setReportType] = useState<string>(''); // 表示上だけ必要
  const [startDate, setStartDate] = useState<string>(prefill?.start_date || '');
  const [endDate, setEndDate] = useState<string>(prefill?.end_date || '');
  const [approver3, setApprover3] = useState<string>('');
  const [approverAM, setApproverAM] = useState<string>(''); // ローカル保持
  const [shikimuke, setShikimuke] = useState<string>('');
  const [lot, setLot] = useState<string>('');
  const [powertrain, setPowertrain] = useState<string>(''); // 表示用に同期する
  const [project, setProject] = useState<string>('');       // Project 表示用に同期する

  const prevTeamRef = useRef<string>('');
  // 直前の「課ID」を覚えておく
  const prevDeptRef = useRef<number | null>(null);

  // チームごとのスナップショットを保持する
  // key: teamId(string)
  // value: そのチームで表示されていた承認AMなど
  // 目的:
  //   Aチーム → Bチーム → Aチーム と戻ったとき、
  //   Aチームで元々(最初に)見えていた承認AM等を復元する
  const teamSnapshotsRef = useRef< 
  Map<
   string, 
  { 
    approver3: string;
    approverAM: string;
    startDate: string;
    endDate: string;
    reportType: string;
    project: string;
    taskMembers: string[];
   } >
    >
  (new Map());

  /* ---------- マスター派生 ---------- */

  // 画面に入った時点のチーム保持する
  // Bで開いたならBのまま。Aに切り替えてもここは更新しない。
  const originalTeamRef = useRef<string | number | null>(team ?? null);

  // 初回だけ originalTeamRef を埋める。以降は上書きしない。
  useEffect(() => {
    if (!originalTeamRef.current && team) {
      originalTeamRef.current = team;
    }
  }, [team]);

  // departments が無い場合に単数 department を参照
  const selectedDeptIds = useMemo(
    () => {
      // 親の division 変更を優先反映（departmentId が与えられた場合）
      if (typeof departmentId === 'number' && departmentId > 0) {
        return [Number(departmentId)];
      }
      const arr = (prefill?.departments ?? [])
        .map((n) => Number(n))
        .filter((n) => Number.isFinite(n));
      if (arr.length > 0) return arr;
      const single = Number(prefill?.department);
      return Number.isFinite(single) && single > 0 ? [single] : [];
    },
    [departmentId, prefill?.departments, prefill?.department]
  );

  // selectedDeptIds の先頭を単数として使う（チーム候補の課フィルタ用）
  const selectedDeptId = useMemo<number | null>(() => {
    const n = Number(selectedDeptIds?.[0]);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [selectedDeptIds]);

  const selectedDeptTeamIds = useMemo(() => {
    const teams: any[] = Array.isArray((master as any)?.teams) ? (master as any).teams : [];
    const scopedTeams =
      typeof selectedDeptId === 'number'
        ? teams.filter((t: any) => Number(t?.department_id ?? t?.dept_id ?? 0) === selectedDeptId)
        : teams;

    return Array.from(
      new Set(
        (scopedTeams ?? [])
          .map((t: any) => norm(t?.team_id ?? t?.id ?? t?.value ?? ''))
          .filter(Boolean)
      )
    );
  }, [master, selectedDeptId]);

  const { userOptions } = useUserOptions();
  const { profile: userProfile } = useUserProfile();

  const userOptionDeptId = useMemo<number | null>(() => {
    if (selectedDeptId !== null) return selectedDeptId;

    const teamsArr: any[] = Array.isArray((master as any)?.teams)
      ? (master as any).teams
      : [];
    const selectedTeamKey = norm(team || prefill?.team || '');
    if (!selectedTeamKey) return null;

    const hit = teamsArr.find(
      (t: any) => norm(t?.team_id ?? t?.id ?? t?.value ?? '') === selectedTeamKey
    );
    const deptId = Number(hit?.department_id ?? hit?.dept_id);
    return Number.isFinite(deptId) && deptId > 0 ? deptId : null;
  }, [master, prefill?.team, selectedDeptId, team]);

  const userOptionDeptCandidates = useMemo(() => {
    const out = new Set<string>();
    const add = (value: any) => {
      const s = norm(value);
      if (s) out.add(s);
    };

    add(userOptionDeptId);

    const deps: any[] = Array.isArray((master as any)?.departments)
      ? (master as any).departments
      : [];
    const dep = deps.find(
      (d: any) => Number(d?.department_id ?? d?.id) === Number(userOptionDeptId)
    );
    [dep?.label, dep?.value, dep?.name, dep?.code].forEach(add);

    return Array.from(out);
  }, [master, userOptionDeptId]);

  const fallbackProfileTeamCandidates = useMemo(() => {
    const out = new Set<string>();
    const add = (value: any) => {
      const s = norm(value);
      if (s) out.add(s);
    };
    const preferredTeamId = norm(userProfile?.preferredTeamId);

    add(userProfile?.preferredTeamLabel);
    add(preferredTeamId);
    add(userProfile?.team);

    if (preferredTeamId) {
      const teamsArr: any[] = Array.isArray((master as any)?.teams)
        ? (master as any).teams
        : [];
      const hit = teamsArr.find(
        (t: any) => norm(t?.team_id ?? t?.id ?? t?.value ?? '') === preferredTeamId
      );
      if (hit) {
        [
          hit?.label,
          hit?.team_name,
          hit?.teamName,
          hit?.team_label,
          hit?.teamLabel,
          hit?.name,
        ].forEach(add);
      }
    }

    return Array.from(out);
  }, [
    master,
    userProfile?.preferredTeamId,
    userProfile?.preferredTeamLabel,
    userProfile?.team,
  ]);

  const preferredTeamCandidates = useMemo(
    () =>
      buildPreferredTeamCandidates({
        users: userOptions as any[],
        selectedTeam: team || prefill?.team || '',
        teams: Array.isArray((master as any)?.teams) ? (master as any).teams : [],
        fallbackTeamCandidates: fallbackProfileTeamCandidates,
      }),
    [fallbackProfileTeamCandidates, master, prefill?.team, team, userOptions]
  );

  const approver3Opts = useMemo(() => {
    const arr = (master?.approvers ?? [])
      .map((x: any) => norm(x?.label ?? x?.value ?? x))
      .filter(Boolean);
    return normalizeOptions(arr.map((s) => ({ value: s, label: s })));
  }, [master]);

  const approverAMOpts = useMemo(() => {
    const arr = (master?.approverAMs ?? [])
      .map((x: any) => norm(x?.label ?? x?.value ?? x))
      .filter(Boolean);
    return normalizeOptions(arr.map((s) => ({ value: s, label: s })));
  }, [master]);

  const coAuthorBaseOpts = useDepartmentUserOptions({
    users: userOptions as any[],
    deptCandidates: userOptionDeptCandidates,
    strict: false,
    preferredTeamCandidates,
    prioritizePreferredTeam: true,
  });

  const officialCoAuthorOptions = useMemo(() => {
    return uniqueOptions([
      ...(coAuthorBaseOpts ?? []).map(toOption),
      ...(((master as any)?.taskMembers ?? []) as any[]).map((x: any) =>
        toOption({ value: x?.value ?? x?.label ?? x, label: x?.label ?? x?.value ?? x })
      ),
    ]);
  }, [coAuthorBaseOpts, master]);

  const {
    respondentOptions: coAuthorRespondentOptions,
    customOptions: coAuthorRespondentCustomOptions,
    addOptions: addCoAuthorRespondentOptions,
  } = useCoAuthorRespondentOptions({
    loginId,
    departmentId: selectedDeptId,
    teamIds: selectedDeptTeamIds,
    officialOptions: officialCoAuthorOptions,
  });

  // 連名者（= respondents）候補
  const coAuthorOpts = useMemo(() => {
    // ---- 1) 現在の課IDを決める（departmentId > selectedDeptIds > team 逆引き）----
    const deptId = (() => {
      if (typeof departmentId === 'number' && departmentId > 0) return Number(departmentId);
      if (selectedDeptIds.length > 0) return Number(selectedDeptIds[0]);

      const teamsArr: any[] = Array.isArray((master as any)?.teams)
        ? (master as any).teams
        : [];
      const hit = teamsArr.find(
        (t: any) => String(t?.team_id ?? t?.id) === String(team)
      );
      const d = Number(hit?.department_id ?? hit?.dept_id);
      return Number.isFinite(d) && d > 0 ? d : 0;
    })();

    const normU = (s?: any) =>
      String(s ?? '').trim().normalize('NFKC').toUpperCase();

    // ---- 2) deptId から課コード（例: XQ4）を引く（取れなければフィルタしない）----
    const deptKeys = (() => {
      const deps: any[] = Array.isArray((master as any)?.departments)
        ? (master as any).departments
        : [];
      const dep = deps.find(
        (d: any) => Number(d?.department_id ?? d?.id) === Number(deptId)
      );

      const set = new Set<string>();
      [dep?.label, dep?.value, dep?.name, dep?.code].forEach((x) => {
        const k = normU(x);
        if (k) set.add(k);
      });
      return set;
    })();

    const out: Array<{ value: string; label: string }> = [
      ...(coAuthorBaseOpts ?? []).map(toOption),
    ];

    const pushOpt = (valueRaw: any, labelRaw: any) => {
      const value = norm(valueRaw ?? '');
      const label = norm(labelRaw ?? '');

      if (!label && !value) return;

      out.push({
        value: value || label,
        label: label || value,
      });
    };

    // ---- 3) API（userOptions）由来：現在の課の所属ユーザーを候補に ----
    // userOptions が {value,label,department} 形式でも、/get_all_user_info の生形式でも拾えるようにする
    const srcUsers: any[] = Array.isArray(userOptions) ? userOptions : [];
    const filteredUsers = deptKeys.size
      ? srcUsers.filter((u: any) => {
          const dep = u?.department ?? u?.dept ?? u?.division;
          return deptKeys.has(normU(dep));
        })
      : srcUsers;

    filteredUsers.forEach((u: any) => {
      const label =
        u?.label ??
        (u?.last_name || u?.first_name
          ? `${String(u?.last_name ?? '')}${String(u?.first_name ?? '')}`
          : u?.name ?? u?.text ?? u?.value);

      const value =
        u?.mail_address ??
        u?.value ??
        u?.id ??
        label;

      pushOpt(value, label);
    });

    // ---- 4) master.taskMembers（互換）----
    const taskMembersSrc = (master as any)?.taskMembers ?? [];
    (Array.isArray(taskMembersSrc) ? taskMembersSrc : []).forEach((x: any) => {
      pushOpt(x?.value ?? x?.label ?? x, x?.label ?? x?.value ?? x);
    });

    return dedupePersonOptions(
      uniqueOptions([
        ...out.map(toOption),
        ...(coAuthorRespondentOptions ?? []).map(toOption),
      ])
    );
  }, [
    master,
    userOptions,
    departmentId,
    selectedDeptIds,
    team,
    coAuthorBaseOpts,
    coAuthorRespondentOptions,
  ]);

  const coAuthorCustomOpts = useMemo(() => {
    const officialKeys = new Set<string>();

    officialCoAuthorOptions.forEach((o) => {
      const v = norm(o.value);
      const l = norm(o.label);
      const k = coAuthorNameKey(l);
      if (v) officialKeys.add(`v:${v}`);
      if (l) officialKeys.add(`l:${l}`);
      if (k) officialKeys.add(`k:${k}`);
    });

    const prefillNames = uniqueNormNames([
      ...parseRespondentNames(prefill?.respondents),
      ...parseRespondentNames(prefill?.respondents_1),
      ...parseRespondentNames(prefill?.task_members),
      ...parseRespondentNames(prefill?.members),
      ...parseRespondentNames(prefill?.taskMembers),
    ]);

    const customCandidates = uniqueOptions(
      mergeCoAuthorOptionsByLabel(
        prefillNames.map((name) => ({ value: name, label: name })),
        (coAuthorRespondentCustomOptions ?? []).map(toOption)
      ).map(toOption)
    );
    return customCandidates.filter((o) => {
      const v = norm(o.value);
      const l = norm(o.label);
      const k = coAuthorNameKey(l);
      return !(officialKeys.has(`v:${v}`) || officialKeys.has(`l:${l}`) || officialKeys.has(`k:${k}`));
    });
  }, [
    officialCoAuthorOptions,
    coAuthorRespondentCustomOptions,
    prefill?.respondents,
    prefill?.respondents_1,
    prefill?.task_members,
    prefill?.members,
    prefill?.taskMembers,
  ]);

const teamsForSelectedDept = useMemo(() => {
  const teams: any[] = Array.isArray((master as any)?.teams) ? (master as any).teams : [];
  return typeof selectedDeptId === 'number'
    ? teams.filter((t: any) => Number(t?.department_id ?? t?.dept_id ?? 0) === selectedDeptId)
    : teams;
}, [master, selectedDeptId]);

const teamOpts = useMemo(() => {
  const filtered = teamsForSelectedDept.filter((t: any) => {
    if (showPastTeams) return true;
    return !isPastTeam(getTeamEndFiscalYear(t));
  });

  return normalizeOptions(
    filtered.map((t: any) => {
      const value = String(t?.team_id ?? t?.teamId ?? t?.id ?? t?.value ?? t);
      const label = String(
        t?.label ??
          t?.name ??
          t?.team_name ??
          t?.teamName ??
          t?.team_label ??
          t?.teamLabel ??
          t?.value ??
          (t?.team_id != null ? String(t.team_id) : String(t))
      );

      const groupLabel = isPastTeam(getTeamEndFiscalYear(t))
        ? '過去チーム'
        : norm(
            t?.groupLabel ??
              t?.team_classification_name ??
              t?.team_class_name ??
              t?.team_classification ??
              t?.class_name ??
              t?.classification_name ??
              ''
          );

      return groupLabel ? { value, label, groupLabel } : { value, label };
    })
  );
}, [teamsForSelectedDept, showPastTeams]);

const teamOptsUnique = useMemo(() => uniqueOptions(teamOpts), [teamOpts]);

useEffect(() => {
  setShowPastTeams(false);
}, [selectedDeptId, prefill?.wr_id]);

useEffect(() => {
  if (!team) return;
  const values = new Set((teamOpts ?? []).map((o) => String(o.value)));
  if (showPastTeams || values.has(String(team))) return;

  const allValues = new Set(
    (teamsForSelectedDept ?? []).map((t: any) =>
      String(t?.team_id ?? t?.teamId ?? t?.id ?? t?.value ?? t)
    )
  );
  if (allValues.has(String(team))) {
    setShowPastTeams(true);
  }
}, [team, teamOpts, teamsForSelectedDept, showPastTeams]);



  /* ---------- 属性（ATTRIBUTE_MANAGE） ---------- */

  const allAttributes: AttrFromMaster[] = useMemo(() => {
    const list = ((master as any)?.attributeManage ?? []) as AttrFromMaster[];
    // OFF行もスコープ優先判定に使うため、ここでは除外しない。
    return (list ?? []).map((x) => ({
      ...x,
      attr_type: toAttrTypeCode(x?.attr_type),
      multiple_select: toMultiFlag(x?.multiple_select),
    }));
  }, [master]);

  const toNumberOrNullLocal = (value: unknown): number | null => {
    if (value == null || value === '') {
      return null;
    }

    const n = Number(value);

    return Number.isFinite(n) ? n : null;
  };

  const toScopeNumberLocal = (value: unknown): number => {
    return toNumberOrNullLocal(value) ?? 0;
  };

  // 現在の team_id / dept_id を確定
  // teamOpts のロード前でも、teamValue/prefill/master.teams から補完する。
  const currentTeamId = useMemo(() => {
    const rawTeam = norm(team ?? teamValue ?? prefill?.team ?? '');
    const directTeamId = toNumberOrNullLocal(rawTeam);

    if (directTeamId != null && directTeamId > 0) {
      return directTeamId;
    }

    const hitFromOptions = findOptionByValueOrLabel(rawTeam, teamOptsUnique);
    const optionTeamId = toNumberOrNullLocal(hitFromOptions?.value);

    if (optionTeamId != null && optionTeamId > 0) {
      return optionTeamId;
    }

    const teams: any[] = Array.isArray((master as any)?.teams)
      ? (master as any).teams
      : [];

    const hitFromMaster = teams.find((t: any) => {
      const candidates = [
        t?.team_id,
        t?.teamId,
        t?.id,
        t?.value,
        t?.label,
        t?.name,
        t?.team_name,
        t?.teamName,
        t?.team_label,
        t?.teamLabel,
      ];

      return candidates.some((candidate) => sameName(String(candidate ?? ''), rawTeam));
    });

    const masterTeamId = toNumberOrNullLocal(
      hitFromMaster?.team_id ??
        hitFromMaster?.teamId ??
        hitFromMaster?.id ??
        hitFromMaster?.value
    );

    return masterTeamId != null && masterTeamId > 0 ? masterTeamId : 0;
  }, [team, teamValue, prefill?.team, teamOptsUnique, master]);

  const currentDeptId = useMemo(() => {
    if (selectedDeptIds.length > 0) return selectedDeptIds[0]!;

    const prefillDeptId = toNumberOrNullLocal(prefill?.department);
    if (prefillDeptId != null && prefillDeptId > 0) return prefillDeptId;

    const teams: any[] = Array.isArray((master as any)?.teams)
      ? (master as any).teams
      : [];

    const hit = teams.find(
      (t: any) => Number(t?.team_id ?? t?.teamId ?? t?.id) === currentTeamId
    );

    return toScopeNumberLocal(hit?.department_id ?? hit?.departmentId ?? hit?.dept_id);
  }, [selectedDeptIds, prefill?.department, master, currentTeamId]);

  const fallbackDeptId = useMemo(() => {
    if (currentDeptId) return currentDeptId;

    const teams: any[] = Array.isArray((master as any)?.teams)
      ? (master as any).teams
      : [];

    const hit = teams.find(
      (t: any) => Number(t?.team_id ?? t?.teamId ?? t?.id) === currentTeamId
    );

    const teamDeptId = toNumberOrNullLocal(hit?.department_id ?? hit?.departmentId ?? hit?.dept_id);

    if (teamDeptId != null && teamDeptId > 0) {
      return teamDeptId;
    }

    const firstTeam = teams[0];

    return toScopeNumberLocal(firstTeam?.department_id ?? firstTeam?.departmentId ?? firstTeam?.dept_id);
  }, [master, currentDeptId, currentTeamId]);

  // 記入者候補
  const baseReporterOpts = useMemo(
    () => normalizeOptions((coAuthorBaseOpts ?? []).map(toOption)),
    [coAuthorBaseOpts]
  );

  const reporterOpts = useMemo(() => {
    const lbl = norm(prefill?.create_by);
    if (!lbl) return baseReporterOpts;

    const exists = baseReporterOpts.some(
      (o) => norm(o.label) === lbl
    );

    return exists
      ? baseReporterOpts
      : uniqueOptions([
          ...baseReporterOpts,
          { value: lbl, label: lbl },
        ]);
  }, [baseReporterOpts, prefill?.create_by]);

  const scopeDeptId = currentDeptId || fallbackDeptId || 0;

  const scopedManagedAttrsSorted = useMemo(
    () =>
      selectManagedAttrsForScope(
        (allAttributes ?? []).filter((attr) => !FIXED_BASE_NAMES.has(attr.attr_name)),
        {
          departmentId: scopeDeptId,
          teamId: currentTeamId,
        }
      ),
    [allAttributes, scopeDeptId, currentTeamId]
  );

  const hiddenScopedAttrNameSet = useMemo(
    () =>
      new Set(
        scopedManagedAttrsSorted
          .filter((attr) => !isManagedAttrVisible(attr))
          .map((attr) => normalizeManagedAttrNameKey(attr.attr_name))
      ),
    [scopedManagedAttrsSorted]
  );

  const allDynamicVisibleSorted = useMemo(() => {
    const candidates = (allAttributes ?? [])
      .filter((attr) => attr?.attr_name)
      .filter((attr) => !FIXED_BASE_NAMES.has(attr.attr_name))
      .filter((attr) => !hiddenScopedAttrNameSet.has(normalizeManagedAttrNameKey(attr.attr_name)));

    const picked = selectManagedAttrsForScope(candidates, {
      departmentId: scopeDeptId,
      teamId: currentTeamId,
      includeUnscopedFallback: true,
    }).filter((attr) => isManagedAttrVisible(attr));

    return picked.sort(
      (a, b) =>
        Number(a.order_number ?? 9999) -
          Number(b.order_number ?? 9999) ||
        a.attr_name.localeCompare(b.attr_name, 'ja')
    );
  }, [
    allAttributes,
    scopeDeptId,
    currentTeamId,
    hiddenScopedAttrNameSet,
  ]);

  const visibleCommonAttrsSorted = useMemo(
    () =>
      allDynamicVisibleSorted.filter((attr) => isCommonDisplayManagedAttr(attr)),
    [allDynamicVisibleSorted]
  );

  const effectiveDynamicAttrsForTeam = useMemo(
    () =>
      allDynamicVisibleSorted.filter((attr) => isFreeInputManagedAttr(attr)),
    [allDynamicVisibleSorted]
  );

  // 解決テーブル
  const effectiveMetaByName = useMemo(() => {
    const m = new Map<string, AttrFromMaster>();
    allDynamicVisibleSorted.forEach((a) => m.set(a.attr_name, a));
    return m;
  }, [allDynamicVisibleSorted]);

  const attrMetaById = useMemo(() => {
    const m = new Map<number, AttrFromMaster>();
    (allAttributes ?? []).forEach((a) =>
      m.set(Number(a.attr_id), a)
    );
    return m;
  }, [allAttributes]);

  const resolveScopedAttrMeta = useCallback(
    (attrName?: string, attrId?: number | string | null): AttrFromMaster | undefined => {
      const deptId = scopeDeptId > 0 ? scopeDeptId : null;
      const teamId = currentTeamId > 0 ? currentTeamId : null;

      const normalizeAttrNameForScope = (value: unknown): string => {
        return String(value ?? '')
          .normalize('NFKC')
          .trim()
          .replace(/\s+/g, '')
          .toLowerCase();
      };

      const knownGlobalCommonAttrByName: Record<string, { id: number; name: string }> = {
        [normalizeAttrNameForScope(FIXED_ATTR_NAME_SHIMUKE)]: { id: 5, name: FIXED_ATTR_NAME_SHIMUKE },
        [normalizeAttrNameForScope(FIXED_ATTR_NAME_LOT)]: { id: 6, name: FIXED_ATTR_NAME_LOT },
      };

      const knownAttrNameById: Record<number, string> = {
        5: FIXED_ATTR_NAME_SHIMUKE,
        145: FIXED_ATTR_NAME_SHIMUKE,
        6: FIXED_ATTR_NAME_LOT,
        146: FIXED_ATTR_NAME_LOT,
      };

      const resolveByName = (rawName?: string): AttrFromMaster | undefined => {
        const nameKey = normalizeAttrNameForScope(rawName);
        if (!nameKey) return undefined;

        const scopedPick = selectManagedAttrsForScope(
          (allAttributes ?? []).filter((attr) => normalizeAttrNameForScope((attr as any)?.attr_name) === nameKey),
          {
            departmentId: deptId,
            teamId,
          }
        )[0];

        if (scopedPick) {
          return isManagedAttrVisible(scopedPick) ? scopedPick : undefined;
        }

        const knownGlobal = knownGlobalCommonAttrByName[nameKey];
        if (!knownGlobal) return undefined;

        const foundGlobal = attrMetaById.get(knownGlobal.id);
        if (
          foundGlobal &&
          selectManagedAttrsForScope([foundGlobal], { departmentId: deptId, teamId }).length > 0 &&
          isManagedAttrVisible(foundGlobal)
        ) {
          return foundGlobal;
        }

        // weekly-input-items のレスポンスに全体共通 attr_id=5/6 が含まれない場合でも、
        // team_id=31 などで別チーム属性 145/146 を送らないための最後の保険。
        return {
          attr_id: knownGlobal.id,
          attr_name: knownGlobal.name,
          department_id: 0,
          team_id: 0,
          attr_type: 11,
          multiple_select: 1,
          order_number: Number.MAX_SAFE_INTEGER,
          status: 'Y',
          options: [],
        } as AttrFromMaster;
      };

      const directByName = resolveByName(attrName);
      if (directByName) {
        return directByName;
      }

      const id = toNumberOrNullLocal(attrId);
      if (id == null) {
        return undefined;
      }

      const sourceMeta = attrMetaById.get(id);
      const sourceName = String(
        sourceMeta?.attr_name ??
          knownAttrNameById[id] ??
          ''
      ).trim();

      const canonicalBySourceName = resolveByName(sourceName);
      if (canonicalBySourceName) {
        return canonicalBySourceName;
      }

      if (!sourceMeta) {
        return undefined;
      }

      const sourcePick = selectManagedAttrsForScope([sourceMeta], { departmentId: deptId, teamId })[0];
      return sourcePick && isManagedAttrVisible(sourcePick) ? sourcePick : undefined;
    },
    [
      scopeDeptId,
      currentTeamId,
      allAttributes,
      attrMetaById,
    ]
  );

  /* ---------- 初期 fields ---------- */

  const initialFields: DynamicFieldItem[] = [
    {
      id: 'reporter-init',
      type: '記入者',
      label: '記入者',
      value: reporter?.trim() ? reporter : '',
      isRequired: true,
      isRemovable: false,
      divisionContext: division,
      options: reporterOpts,
    },
    {
      id: 'taskmembers-base',
      type: '連名者',
      label: '連名者',
      value: [],
      isRemovable: false,
      divisionContext: division,
    },
    {
      id: 'approveram-init',
      type: '承認AM',
      label: 'AM',
      value: approverAM ?? '',
      isRemovable: false,
      divisionContext: division,
      options: approverAMOpts,
    },
    {
      id: 'daterange-init',
      type: '開始終了日付',
      label: '開始日付',
      value: { from: startDate || '', to: endDate || '' },
      isRequired: true,
      isRemovable: false,
      divisionContext: division,
    },
  ];

  const [fields, setFields] = useState<DynamicFieldItem[]>(initialFields);

  /* ---------- 受け取った prefill ログ ---------- */
  useEffect(() => {
    const usage: any = {
      hasPrefill: !!prefill,
      used: {
        create_by: false,
        team: false,
        start_date: false,
        end_date: false,
        approver: false,
        approve_am: false,
        respondents: false,
        report_typesMatched: 0,
      },
      raw: prefill,
    };

    if (prefill?.create_by) {
      const hit = reporterOpts.find(
        (o) => o.label === norm(prefill.create_by)
      );
      usage.used.create_by = !!hit;
    }
    if (prefill?.team != null) {
      const key = String(prefill.team);
      const byId = teamOpts.find(
        (o) => o.value === key || o.label === key
      );
      usage.used.team = !!byId;
    }
    if (prefill?.start_date) usage.used.start_date = true;
    if (prefill?.end_date) usage.used.end_date = true;
    if (prefill?.approver) {
      const hit = approver3Opts.find(
        (o) =>
          o.label === norm(prefill.approver) ||
          o.value === norm(prefill.approver)
      );
      usage.used.approver = !!hit;
    }
    if (prefill?.approve_am) {
      const hit = approverAMOpts.find(
        (o) =>
          o.label === norm(prefill.approve_am) ||
          o.value === norm(prefill.approve_am)
      );
      usage.used.approve_am = !!hit;
    }
    if (prefill?.respondents) usage.used.respondents = true;

    if (prefill?.report_types?.length) {
      const meta = effectiveMetaByName.get(CLASSIFICATION_MARKET_TARGET_ATTR_NAME);
      if (meta) {
        const optsFromBuckets = optionsFromBuckets(
          master,
          meta.attr_name,
          Number(meta.attr_id),
          currentDeptId || 0,
          currentTeamId || 0
        );
        const opts = uniqueOptions(
          (optsFromBuckets.length
            ? optsFromBuckets
            : normalizeOptions(meta.options)
          ).map(toOption)
        );
        let matched = 0;
        (prefill.report_types ?? []).forEach((raw) => {
          const s = String(raw);
          const byValue = opts.find(
            (o) => norm(o.value) === norm(s)
          );
          const byLabel = opts.find(
            (o) => norm(o.label) === norm(s)
          );
          if (byValue || byLabel) matched++;
        });
        usage.used.report_typesMatched = matched;
      }
    }
  }, [
    prefill,
    reporterOpts,
    teamOpts,
    effectiveMetaByName,
    master,
    approver3Opts,
    approverAMOpts,
    currentDeptId,
    currentTeamId,
  ]);

  /* ---------- プレフィル反映（固定系） ---------- */

  // 記入者
  // ユーザーが一度でも操作した後は、プレフィルや候補更新で上書きしない
  useEffect(() => {
    if (userTouchedReporterRef.current) return;

    if (!prefill?.create_by) {
      setReporter('');
      return;
    }
    const lbl = norm(prefill.create_by);
    const hit = reporterOpts.find(
      (o) => norm(o.label) === lbl
    );
    setReporter(hit ? hit.value : lbl);
  }, [prefill?.create_by, reporterOpts]);

  // 新しいレコードを選択したときにのみ「触った」フラグをリセット
  useEffect(() => {
    userTouchedReporterRef.current = false;
  }, [prefill?.create_by]);

  // 課（department）変更時は「記入者」の自動上書き抑止フラグをリセット
  useEffect(() => {
    userTouchedReporterRef.current = false;
  }, [departmentId]);

  // 部門フィルタにより候補が変わった際、現在の記入者が新候補に無ければ空へクリア（ユーザー操作済みなら維持）
  useEffect(() => {
    if (userTouchedReporterRef.current) return;
    setReporter((prev) => {
      if (!prev) return prev;
      const exists = reporterOpts.some(
        (o) =>
          o.value === prev ||
          norm(o.label) === norm(prev)
      );
      return exists ? prev : '';
    });
  }, [reporterOpts]);

  // チーム
  // 1) 新しいレコードを選択したとき：
  //    - モーダルで別レコードを選んだら毎回リセット
  //    - 「ユーザーが手でいじったか」フラグもリセット
  //    - そのレコードに team が無い場合はチームを空にする
  const prevPrefillKeyRef = useRef<string>('');

  // 1) prefill の変化検知キー（attribute_values の “中身” を含める）
  // ここを変えないと「内報」(2文字)→「外報」(2文字) みたいなケースで
  // “同一扱い” になり touched リセットが走らず、DB反映が抑止されて内報が残ります。
  const makePrefillKey = (p?: Prefill) =>
    JSON.stringify({
      team: p?.team ?? null,
      start: p?.start_date ?? null,
      end: p?.end_date ?? null,
      create_by: p?.create_by ?? null,
      report_types: p?.report_types ?? null,
      attribute_values: p?.attribute_values ?? null,
      attributes: p?.attributes ?? null,
      attributeValues: p?.attributeValues ?? null,
      base_attributes: p?.base_attributes ?? null,
      baseAttributes: p?.baseAttributes ?? null,
      weekly_report_base_attributes: p?.weekly_report_base_attributes ?? null,
      project: p?.project ?? null,
      projects: p?.projects ?? null,
    });

  useEffect(() => {
    const k = makePrefillKey(prefill);
    if (k === prevPrefillKeyRef.current) return;
    prevPrefillKeyRef.current = k;

    // レコードが“本当に”切り替わった時だけ初期化
    userTouchedTeamRef.current = false;
    visitedTeamsRef.current.clear();
    blockBackfillTeamsRef.current.clear();

    resetTouchedTypes(); // 新レコードでは touched をリセット

    const controlledTeam = String(teamValue ?? '').trim();
    if (!controlledTeam && prefill?.team == null && team !== '') {
      setTeam('');
      onTeamChange?.(null);
    }
  }, [prefill, onTeamChange, team, teamValue]);

  useEffect(() => {
    // ユーザーが触った後は上書きしない
    if (userTouchedTeamRef.current) return;

    const controlledTeam = String(teamValue ?? '').trim();
    const prefillTeamRaw = controlledTeam || prefill?.team;

    const hit =
      prefillTeamRaw != null
        ? teamOpts.find(
            (o) =>
              o.value === String(prefillTeamRaw) ||
              o.label === String(prefillTeamRaw)
          )
        : undefined;

    const nextVal = hit ? hit.value : (prefillTeamRaw != null ? String(prefillTeamRaw) : '');

    // ★新レコード選択時に「前レコードの team が残っていて注入がスキップされる」事故を防ぐ
    if (team !== nextVal) {
      setTeam(nextVal);
      onTeamChange?.(nextVal ? nextVal : null);
    }
  }, [prefill?.team, teamValue, teamOpts, team, onTeamChange]);


  // 3) 候補の変化で現在の team が存在しなくなったらクリア（部門変更などで発生）
  useEffect(() => {
    if (!team) return;

    const existsInMaster = Array.isArray((master as any)?.teams)
      ? (master as any).teams.some(
          (t: any) =>
            String(t?.team_id ?? t?.id) === String(team)
        )
      : false;

    const existsInOptions = (teamOpts ?? []).some(
      (o) => o.value === team
    );

    // ユーザー操作後は options に無くても保持（課フィルタの都合で落ちているだけなら維持）
    if (userTouchedTeamRef.current) {
      if (!existsInMaster) {
        setTeam('');
        onTeamChange?.(null);
      }
      return;
    }

    // ユーザー未操作時は従来どおり options に無ければクリア
    if (!existsInOptions) {
      setTeam('');
      onTeamChange?.(null);
    }
  }, [teamOpts, team, master, onTeamChange]);

  // 期間
  useEffect(() => {
    setStartDate(prefill?.start_date ?? '');
    setEndDate(prefill?.end_date ?? '');
  }, [prefill?.start_date, prefill?.end_date]);

  // 承認-3
  useEffect(() => {
    if (userTouchedTeamRef.current) return; // チーム変更直後はプレフィル再注入を抑止
    if (!prefill?.approver) {
      setApprover3('');
      return;
    }
    const hit = approver3Opts.find(
      (o) =>
        o.label === norm(prefill.approver) ||
        o.value === norm(prefill.approver)
    );
    setApprover3(hit ? hit.value : '');
  }, [prefill?.approver, approver3Opts]);

  // 承認AM
  useEffect(() => {
    if (userTouchedTeamRef.current) return; //チーム変更直後はプレフィル再注入を抑止
    if (!prefill?.approve_am) {
      setApproverAM('');
      onApproverAMChange?.('');
      return;
    }
    const hit = approverAMOpts.find(
      (o) =>
        o.label === norm(prefill.approve_am) ||
        o.value === norm(prefill.approve_am)
    );
    if (hit) {
      setApproverAM(hit.value);
      onApproverAMChange?.(hit.value);
    } else {
      setApproverAM('');
      onApproverAMChange?.('');
    }
  }, [prefill?.approve_am, approverAMOpts, onApproverAMChange]);

  // 連名者
  useEffect(() => {
    const members = limitTaskMembers(uniqueNormNames([
      ...parseRespondentNames(prefill?.respondents),
      ...parseRespondentNames(prefill?.respondents_1),
      ...parseRespondentNames(prefill?.task_members),
      ...parseRespondentNames(prefill?.members),
      ...parseRespondentNames(prefill?.taskMembers),
    ]));

    addCoAuthorRespondentOptions(members.map((name) => ({ value: name, label: name })));

    if (members.length === 0) {
      setFields((prev) =>
        prev.map((f) =>
          f.type === '連名者' || f.type === '課題メンバー'
            ? { ...f, type: '連名者', label: '連名者', value: [] }
            : f
        )
      );
      return;
    }

    setFields((prev) =>
      prev.map((f) =>
        f.type === '連名者' || f.type === '課題メンバー'
          ? { ...f, type: '連名者', label: '連名者', value: members }
          : f
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    prefill?.respondents,
    prefill?.respondents_1,
    prefill?.task_members,
    prefill?.members,
    prefill?.taskMembers,
    addCoAuthorRespondentOptions,
  ]);

  // 週報分類（prefill 値）
  useEffect(() => {

    const meta = effectiveMetaByName.get(CLASSIFICATION_MARKET_TARGET_ATTR_NAME);
    if (!meta) return;

    // DB(attribute_values) に週報分類が入っているなら、report_types 側で上書きしない
    if (prefillHasAttrId(prefill?.attribute_values, Number(meta.attr_id))) {
      return;
    }

    if (!prefill?.report_types?.length) return;
    if (userTouchedTeamRef.current) return; // ユーザーがチームを触ったら以後は適用しない

    // チーム変更直後は prefill をスキップ
    if (prevTeamRef.current && prevTeamRef.current !== team) {
      return;
    }


    const optsFromBuckets = optionsFromBuckets(
      master,
      meta.attr_name,
      Number(meta.attr_id),
      currentDeptId || 0,
      currentTeamId || 0
    );
    const opts = uniqueOptions(
      (optsFromBuckets.length
        ? optsFromBuckets
        : normalizeOptions(meta.options)
      ).map(toOption)
    );

    const knownReportTypeValues = opts.flatMap((option) => [
      option.label,
      option.value,
    ]);
    const restoredReportTypes = (prefill.report_types ?? []).flatMap((raw) =>
      resolveKnownMultiSelectTokens(raw, knownReportTypeValues, {
        onlyKnown: true,
      })
    );

    const picked = new Set<string>();
    restoredReportTypes.forEach((s) => {
      const byValue = opts.find(
        (o) => norm(o.value) === norm(s)
      );
      if (byValue) picked.add(byValue.value);
      else {
        const byLabel = opts.find(
          (o) => norm(o.label) === norm(s)
        );
        if (byLabel) picked.add(byLabel.value);
      }
    });

    // マッチが無ければ反映しない
    if (picked.size === 0) {
      return;
    }

    setFields((prev) =>
      prev.map((f) => {
        if (f.type !== CLASSIFICATION_MARKET_TARGET_ATTR_NAME) return f;

        // 週報分類は常に配列運用に統一
        const optIndex = buildOptionIndex(opts);

        // prefill.report_types を「ラベル優先」で option に寄せる
        const mapped = restoredReportTypes
          .map((raw) => {
            const txt = String(raw);
            const hit = pickOptionPreferLabel(txt, optIndex);
            if (hit) return { value: hit.value, label: hit.label };
            return null;
          })
          .filter(Boolean) as Array<{ value: string; label: string }>;

        if (mapped.length === 0) return f;

        // 念のため正規化（数値迷い込み等の既存対策を再利用）
        const normalized = sanitizeSelectArrayByOptions(mapped, opts, false);

        if (!f.isRemovable) {
          setReportType(normalized.map((x) => x.label).filter(Boolean).join(','));
        }

        return {
          ...f,
          options: opts,
          attr_id: Number(meta.attr_id),
          value: normalized,
        };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    prefill?.report_types,
    master,
    effectiveMetaByName,
    currentDeptId,
    currentTeamId,
    team,
  ]);

  // 週報分類（prefill 無い/空 → クリア）
  useEffect(() => {
    if (userTouchedTeamRef.current) return;
    if (
      prefill &&
      (!prefill.report_types ||
        prefill.report_types.length === 0)
    ) {
      const meta = effectiveMetaByName.get(CLASSIFICATION_MARKET_TARGET_ATTR_NAME);
      if (!meta) return;

      const def: any = [];

      setReportType('');
      setFields((prev) =>
        prev.map((f) =>
          f.type === CLASSIFICATION_MARKET_TARGET_ATTR_NAME && !f.isRemovable
            ? { ...f, value: def }
            : f
        )
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill?.report_types, effectiveMetaByName]);

  /* Project のプレフィル反映（週報分類と同等） */
  useEffect(() => {
    const rawListOrig: string[] = Array.isArray(prefill?.projects)
      ? (prefill!.projects as Array<string | number>).map((x) => String(x))
      : prefill?.project != null
      ? [String(prefill!.project)]
      : [];

    if (rawListOrig.length === 0) return;
    if (userTouchedTeamRef.current) return;
    // [FIX-4] 直前にチームが切り替わった直後は、旧レコードの Project が入り込まないように一度スキップ
    if (prevTeamRef.current && prevTeamRef.current !== team) return;

    const meta = effectiveMetaByName.get(FIXED_ATTR_NAME_PROJECT);
    if (!meta) return;

    const optsFromBuckets = optionsFromBuckets(master, meta.attr_name, Number(meta.attr_id));
    const opts = uniqueOptions((optsFromBuckets.length ? optsFromBuckets : normalizeOptions(meta.options)).map(toOption));

    const picked = new Set<string>();
    rawListOrig.forEach((raw) => {
      const s = String(raw);
      const byValue = opts.find((o) => norm(o.value) === norm(s));
      if (byValue) picked.add(byValue.value);
      else {
        const byLabel = opts.find((o) => norm(o.label) === norm(s));
        if (byLabel) picked.add(byLabel.value);
      }
    });

    if (picked.size === 0) return;

    setFields((prev) =>
      prev.map((f) => {
        if (f.type !== FIXED_ATTR_NAME_PROJECT) return f;
        const isMulti = toMultiFlag(meta?.multiple_select) === 1 || toAttrTypeCode(meta?.attr_type) !== 11;
        if (isMulti) {
          const arr = Array.from(picked).map((v) => {
            const lbl = opts.find((o) => o.value === v)?.label ?? v;
            return { value: v, label: lbl };
          });
          if (!f.isRemovable) setProject(arr.map((x) => x.label).join(','));
          return { ...f, options: opts, attr_id: Number(meta.attr_id), value: arr };
        } else {
          const v = Array.from(picked)[0] ?? '';
          const lbl = opts.find((o) => o.value === v)?.label ?? v;
          if (!f.isRemovable) setProject(lbl || v);
          return v
            ? { ...f, options: opts, attr_id: Number(meta.attr_id), value: { value: v, label: lbl } }
            : { ...f, options: opts, attr_id: Number(meta.attr_id), value: '' };
        }
      })
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill?.projects, prefill?.project, master, effectiveMetaByName, team]);


  // Project（prefill 無い/空 → クリア）
  useEffect(() => {
    const hasExplicitEmpty =
      (Array.isArray(prefill?.projects) &&
        prefill!.projects.length === 0) ||
      (prefill && prefill.project === '');
    if (!hasExplicitEmpty) return;

    const meta = effectiveMetaByName.get(FIXED_ATTR_NAME_PROJECT);
    if (!meta) return;

    const def =
      toAttrTypeCode(meta.attr_type) === 11 &&
      toMultiFlag(meta.multiple_select) === 0
        ? { value: '', label: '' }
        : [];

    setProject('');
    setFields((prev) =>
      prev.map((f) =>
        f.type === FIXED_ATTR_NAME_PROJECT && !f.isRemovable
          ? { ...f, value: def }
          : f
      )
    );
  }, [prefill?.projects, prefill?.project, effectiveMetaByName]);

  // 承認-3の単一ソース化: approver3 を真とし、fields 内の "承認-3" は常に空に統一
  useEffect(() => {
    setFields((prev) => {
      let changed = false;
      const next = prev.map((f) => {
        if (f.type !== '承認-3') return f;
        const emptyVal =
          typeof f.value === 'object' && f.value !== null
            ? { value: '', label: '' }
            : '';
        const cur = pickValueLike(f.value);
        // payload 組み立て時に "fields の承認-3" を参照しても値が乗らないよう、常に空にする
        if (cur !== '') {
          changed = true;
          return { ...f, value: emptyVal };
        }
        return f;
      });
      return changed ? next : prev;
    });
  }, [approver3]);

  /* ---------- ヘルパ ---------- */

  const clsNameOf = (type: string) =>
    clsMap[type] ?? `free-${slugify(type)}`;

  const defaultValueForAttr = (
    attrType?: number | string,
    multiple?: number | string | boolean
  ) => {
    const t = toAttrTypeCode(attrType);
    const multi = toMultiFlag(multiple);
    if (t === 5 || t === 6 || t === 3 || t === 4) return '';
    if (t === 12) return [];
    if (t === 11) return multi === 1 ? [] : { value: '', label: '' };
    if (t === 13) return { value: '', label: '' };
    return '';
  };

  const sameOptions = (
    a?: { value: string; label: string }[],
    b?: { value: string; label: string }[]
  ) =>
    a === b ||
    (!!a &&
      !!b &&
      a.length === b.length &&
      a.every(
        (x, i) =>
          x.value === b[i].value &&
          x.label === b[i].label
      ));


  /**
   * CommonSelect 版 単一セレクト
   * - options に存在しない値（例: "内報 / 外報 / 内々報"）でも表示できるように
   *   その値を options に注入してから描画する
   */
  const simpleSelect = (f: DynamicFieldItem) => {
    // 空要素はまだ入れない（sanitize は “実候補” でやる）
    const baseOpts0 = uniqueOptions((f.options ?? []).map(toOption));

    // まず value を options に寄せて正規化（未知値は {value,label} で返る）
    const sanitized0 = sanitizeSingleSelectByOptions(f.value, baseOpts0);

    // 未知値でも表示できるよう options に注入
    const baseOpts1 = ensureOptionExistsInOptions(baseOpts0, sanitized0);

    // ここで空要素を先頭に追加（未選択へ戻せる）
    const baseOpts = withEmpty(baseOpts1, f.label ?? f.type);

    const selected: CommonSelectOption | null = sanitized0 === '' ? null : sanitized0;

    const apply = (opt: CommonSelectOption | null) => {
      markFieldTouched(f.type);

      // 選択結果も sanitize（数値ID迷い込みは '' になる）
      const nextSanitized = sanitizeSingleSelectByOptions(opt, baseOpts1);

      const v = nextSanitized === '' ? '' : norm(nextSanitized.value);
      const resolvedLabel = nextSanitized === '' ? '' : norm(nextSanitized.label) || v;

      // 次の描画でも表示できるよう、ここでも options へ注入して fields に保持
      const nextOptsPersist =
        nextSanitized === ''
          ? baseOpts0
          : ensureOptionExistsInOptions(baseOpts0, { value: v, label: resolvedLabel });

      if (!f.isRemovable) {
        if (f.type === 'チーム') {
          userTouchedTeamRef.current = true;
          setTeam(v);
          onTeamChange?.(v === '' ? null : v);
        }
        if (f.type === '記入者') {
          userTouchedReporterRef.current = true;
          setReporter(v);
        }
        if (f.type === '承認-3') {
          setApprover3(v);
          onApprover3Change?.(v);
        }
        if (f.type === '承認AM') {
          setApproverAM(v);
          onApproverAMChange?.(v);
        }

        // 表示用 state は label 優先
        if (f.type === CLASSIFICATION_MARKET_TARGET_ATTR_NAME) setReportType(resolvedLabel || v);
        if (f.type === FIXED_ATTR_NAME_SHIMUKE) setShikimuke(resolvedLabel || v);
        if (f.type === FIXED_ATTR_NAME_LOT) setLot(resolvedLabel || v);
        if (f.type === FIXED_ATTR_NAME_POWERTRAIN) setPowertrain(resolvedLabel || v);
        if (f.type === FIXED_ATTR_NAME_PROJECT) setProject(resolvedLabel || v);
      }

      // 内部保持は「空は ''」、値ありは {value,label}
      const nextValue = v ? { value: v, label: resolvedLabel || v } : '';

      setFields((p) =>
        p.map((x) =>
          x.id === f.id
            ? {
                ...x,
                value: nextValue,
                // ここが超重要：未知値を含む options を field 側に保持しておく
                options: nextOptsPersist,
              }
            : x
        )
      );
    };

    return (
      <CommonSelect
        id={f.id}
        options={baseOpts}
        value={selected}
        placeholder={`-- ${f.label ?? f.type} --`}
        allowFreeText={f.type === '記入者'}
        commitInputOnBlur={f.type === '記入者'}
        optionDescriptions={
          isWeeklyReportType(f.type) ? reportTypeScopeDisplayByOptionId : undefined
        }
        defaultOptionDescription={
          isWeeklyReportType(f.type) ? DEFAULT_PUBLIC_SCOPE_DISPLAY : undefined
        }
        onChange={apply}
      />
    );
  };



  const renderInput = (
    f: DynamicFieldItem,
    attrType?: number | string
  ) => {
    const t = toAttrTypeCode(attrType);

    if (t === 2) {
      return (
        <textarea
          id={f.id}
          value={String(f.value ?? '')}
          rows={4}
          onChange={(e) =>
            setFields((p) =>
              p.map((x) =>
                x.id === f.id
                  ? { ...x, value: e.target.value }
                  : x
              )
            )
          }
          style={{
            width: '100%',
            padding: '8px 10px',
            border: isDark ? '1px solid #334155' : '1px solid #ddd',
            borderRadius: 8,
          }}
        />
      );
    }

    if (t === 3) {
      return (
        <input
          id={f.id}
          type="text"
          inputMode="numeric"
          pattern="-?\\d*"
          value={String(f.value ?? '')}
          onChange={(e) =>
            setFields((p) =>
              p.map((x) =>
                x.id === f.id
                  ? {
                      ...x,
                      value: sanitizeIntStrict(
                        e.target.value
                      ),
                    }
                  : x
              )
            )
          }
          style={{
            width: '100%',
            height: 36,
            padding: '6px 10px',
            border: isDark ? '1px solid #334155' : '1px solid #ddd',
            borderRadius: 8,
          }}
        />
      );
    }

    if (t === 5) {
      return (
        <FreeDatePicker
          id={f.id}
          freeOptionsList={[
            { label: f.label ?? '日付' } as OptionsBase,
          ]}
          value={String(f.value ?? '')}
          onChange={(out: any) =>
            setFields((p) =>
              p.map((x) =>
                x.id === f.id
                  ? {
                      ...x,
                      value: String(out ?? ''),
                    }
                  : x
              )
            )
          }
        />
      );
    }

    if (t === 6) {
      return (
        <input
          id={f.id}
          type="time"
          value={String(f.value ?? '')}
          onChange={(e) =>
            setFields((p) =>
              p.map((x) =>
                x.id === f.id
                  ? { ...x, value: e.target.value }
                  : x
              )
            )
          }
          style={{
            width: '100%',
            height: 36,
            padding: '6px 10px',
            border: isDark ? '1px solid #334155' : '1px solid #ddd',
            borderRadius: 8,
          }}
        />
      );
    }

    if (t === 4) {
      const toAsciiDecimal = (raw: string) => {
        let s = (raw ?? '')
          .toString()
          .normalize('NFKC')
          .replace(/[,，、。]/g, '.')
          .replace(/[^\d.\-]/g, '');
        const dot = s.indexOf('.');
        if (dot !== -1)
          s =
            s.slice(0, dot + 1) +
            s
              .slice(dot + 1)
              .replace(/\./g, '');
        const neg = s.startsWith('-');
        s = s.replace(/-/g, '');
        return (neg ? '-' : '') + s;
      };

      return (
        <input
          id={f.id}
          type="text"
          inputMode="decimal"
          value={String(f.value ?? '')}
          onChange={(e) =>
            setFields((p) =>
              p.map((x) =>
                x.id === f.id
                  ? {
                      ...x,
                      value: toAsciiDecimal(
                        e.target.value
                      ),
                    }
                  : x
              )
            )
          }
          style={{
            width: '100%',
            height: 36,
            padding: '6px 10px',
            border: isDark ? '1px solid #334155' : '1px solid #ddd',
            borderRadius: 8,
          }}
        />
      );
    }

    return (
      <input
        id={f.id}
        type="text"
        value={String(f.value ?? '')}
        onChange={(e) =>
          setFields((p) =>
            p.map((x) =>
              x.id === f.id
                ? { ...x, value: e.target.value }
                : x
            )
          )
        }
        style={{
          width: '100%',
          height: 36,
          padding: '6px 10px',
          border: isDark ? '1px solid #334155' : '1px solid #ddd',
          borderRadius: 8,
        }}
      />
    );
  };


  // multi-select 描画時に value が単一形式で来ても壊れないようにする
  const asArrayValue = (v: any): any[] => {
    if (Array.isArray(v)) return v;
    if (v == null) return [];
    if (typeof v === 'string' && norm(v) === '') return [];
    return [v];
  };

  const isWeeklyReportType = (name?: string) => norm(name) === CLASSIFICATION_MARKET_TARGET_ATTR_NAME;

  const renderDynamic = (
    f: DynamicFieldItem
  ): React.ReactNode => {
    const metaByName = effectiveMetaByName.get(f.type);
    const meta = f.attr_id
      ? attrMetaById.get(Number(f.attr_id)) ??
        metaByName
      : metaByName;
  const t = toAttrTypeCode(meta?.attr_type);

  const optsBase = uniqueOptions((f.options ?? []).map(toOption));

  //「共通」を先頭へ
  const opts = f.type === FIXED_ATTR_NAME_POWERTRAIN || f.type === FIXED_ATTR_NAME_LOT || f.type === FIXED_ATTR_NAME_SHIMUKE
    ? ensureCommonOptionAtTop(optsBase)
    : optsBase;

    // 11: select（multiple_select=1 で複数、0 で単一）
    if (t === 11) {
      // 週報分類は常に複数扱い（DBが "内報 / 外報 / 内々報" のように複数を1文字列で持ってくるため）
      // さらに、既に配列が入っている場合も multi として描画する
      const isMulti =
        toMultiFlag(meta?.multiple_select) === 1 ||
        isWeeklyReportType(f.type) ||
        Array.isArray(f.value);

      if (!isMulti) return simpleSelect({ ...f, options: opts });

      // 必ず配列として扱う
      const rawMulti = asArrayValue(f.value);
      const allowUnknown = !isWeeklyReportType(f.type);
      const current = sanitizeSelectArrayByOptions(rawMulti, opts, allowUnknown);

      return (
        <CheckboxDropdownSelect
          id={f.id}
          placeholder={`-- ${f.label ?? f.type} --`}
          options={opts}
          value={current}
          onChange={(next) => {
            markFieldTouched(f.type);
            const normalized = sanitizeSelectArrayByOptions(next, opts, allowUnknown);

            setFields((p) =>
              p.map((x) => (x.id === f.id ? { ...x, value: normalized } : x))
            );

            if (!f.isRemovable) {
              const labels = normalized
                .map((s) => s.label)
                .filter(Boolean)
                .join(',');

              if (f.type === FIXED_ATTR_NAME_SHIMUKE) setShikimuke(labels);
              if (f.type === FIXED_ATTR_NAME_LOT) setLot(labels);
              if (f.type === CLASSIFICATION_MARKET_TARGET_ATTR_NAME) setReportType(labels);
              if (f.type === FIXED_ATTR_NAME_POWERTRAIN) setPowertrain(labels);
              if (f.type === FIXED_ATTR_NAME_PROJECT) setProject(labels);
            }
          }}
          enableSearch
          searchPlaceholder="キーワードで絞り込み"
          showSelectAll={false}
          optionDescriptions={
            isWeeklyReportType(f.type) ? reportTypeScopeDisplayByOptionId : undefined
          }
          defaultOptionDescription={
            isWeeklyReportType(f.type) ? DEFAULT_PUBLIC_SCOPE_DISPLAY : undefined
          }
        />
      );
    }

    // 12: checkbox（MultiSelectWithCheckboxes）
    if (t === 12) {
      const suggestions: Suggestion[] =
        toSuggestions(opts);

      // f.value は「ラベルの配列」を保持
      const labelList: string[] = Array.isArray(f.value)
        ? f.value
            .map((v) =>
              typeof v === 'string'
                ? v
                : String(v)
            )
            .map(norm)
            .filter(Boolean)
        : [];

      const initialSelected: Suggestion[] =
        labelList.map((lbl, idx) => {
          const hit = suggestions.find(
            (s) => norm(s.label) === norm(lbl)
          );
          return (
            hit ?? {
              id: -(idx + 1),
              label: lbl,
            }
          ); // 候補に無いラベルは負IDで保持
        });

      return (
        <MultiSelectWithCheckboxes
          suggestions={suggestions}
          placeholder={`-- ${f.label} --`}
          initialValue={initialSelected}
          onChange={(vals: Suggestion[]) =>
            setFields((p) =>
              p.map((x) =>
                x.id === f.id
                  ? {
                      ...x,
                      value: (vals ?? []).map(
                        (s) => s.label
                      ).map(norm),
                    }
                  : x
              )
            )
          }
        />
      );
    }

    // 13: datalist
    if (t === 13) {
      const listId = `${f.id}-datalist`;
      const display =
        typeof f.value === 'object'
          ? String(
              (f.value as any).label ??
                (f.value as any).value ??
                ''
            )
          : String(f.value ?? '');
      const map = new Map(
        opts.map((o) => [o.label, o.value])
      );
      const openPicker = (input: HTMLInputElement) => {
        try {
          (input as any).showPicker?.();
        } catch {
          /* noop */
        }
      };

      return (
        <div className="datalist-wrapper">
          <input
            id={f.id}
            type="text"
            list={listId}
            value={display}
            placeholder={f.label}
            onChange={(
              e: React.ChangeEvent<HTMLInputElement>
            ) => {
              const txt = e.target.value;
              setFields((p) =>
                p.map((x) =>
                  x.id === f.id
                    ? {
                        ...x,
                        value: map.has(txt)
                          ? {
                              value: map.get(
                                txt
                              )!,
                              label: txt,
                            }
                          : txt,
                      }
                    : x
                )
              );
            }}
            onKeyDown={(
              e: React.KeyboardEvent<HTMLInputElement>
            ) => {
              if (
                (e.key === 'ArrowDown' ||
                  e.key === 'F4') &&
                SUPPORTS_DATALIST_PICKER
              ) {
                const el =
                  datalistInputRefs.current[
                    f.id
                  ];
                if (el) openPicker(el);
              }
            }}
            onMouseDown={(
              e: React.MouseEvent<HTMLInputElement>
            ) => {
              const input =
                e.currentTarget as HTMLInputElement;
              if (SUPPORTS_DATALIST_PICKER)
                openPicker(input);
            }}
            ref={(el: HTMLInputElement | null) => {
              datalistInputRefs.current[f.id] = el;
            }}
            autoComplete="off"
          />
          <datalist id={listId}>
            {opts.map((o, idx) => (
              <option
                key={`${o.value}__${idx}`}
                value={o.label}
                data-value={o.value}
              />
            ))}
          </datalist>
        </div>
      );
    }

    // それ以外（text/textarea/int/decimal/date/time）
    return renderInput(f, meta?.attr_type);
  };

  // ========================
  // 個別フィールド描画
  // ========================
  const wrapField = (f: DynamicFieldItem) => {
    const cls = clsNameOf(f.type);

    const labelEl =
      f.type !== '開始終了日付' &&
      f.type !== '記入者' ? (
        <label
          htmlFor={f.id}
          className={f.isRequired ? 'required-label' : ''}
        >
          {f.label}&nbsp;{SyncIcon}
        </label>
      ) : f.type === '記入者' ? (
        <div className="start-date-icon">
          <label
            htmlFor={f.id}
            className={f.isRequired ? 'required-label' : ''}
          >
            {f.label}
          </label>
          &nbsp;{SyncIcon}
        </div>
      ) : null;

    // いま RegisteredInfo は何も出していないが、将来拡張のために残す
    const RegisteredInfo: React.FC = () => {
      if (
        f.type === '記入者' ||
        f.type === '開始終了日付'
      )
        return null;
      return null;
    };

    return (
      <div
        key={f.id}
        className={`form-field dynamic-field-wrapper add-parent-${cls}`}
      >
        {labelEl}
        <div style={{ position: 'relative' }}>
          {['チーム', '記入者', '承認-3', '承認AM'].includes(
            f.type
          ) ? (
            <>
              {simpleSelect(f)}
              {f.type !== '記入者' && <RegisteredInfo />}
            </>
            ) : f.type === '連名者' ? (
              (() => {
                const memberOpts = dedupeByNameKey(uniqueOptions((coAuthorOpts ?? []).map(toOption)));

                // fields は string[] で保持している前提（既存互換）
                const currentNames = Array.isArray(f.value) ? limitTaskMembers(uniqueNormNames(f.value)) : [];

                // CheckboxDropdownSelect 用に options に寄せた {value,label}[] に変換
                const currentSelected = uniqueOptions(
                  currentNames
                    .map((name) => {
                      const hit = memberOpts.find(
                        (o) => norm(o.label) === norm(name) || norm(o.value) === norm(name)
                      );
                      return hit ? { value: hit.value, label: hit.label } : { value: name, label: name };
                    })
                    .filter((o) => norm(o.value) || norm(o.label))
                );

                return (
                  <CheckboxDropdownSelectWithAll
                    id={f.id}
                    placeholder={`-- ${f.label ?? f.type} --`}
                    options={memberOpts}
                    customOptions={coAuthorCustomOpts}
                    value={currentSelected}
                    onChange={(next: Array<{ value: string; label: string }>) => {
                      if (readOnly) return;

                      markFieldTouched(f.type);
                      // fields 側は「名前（label）」で保持（既存互換）
                      const names = limitTaskMembers(uniqueNormNames(
                        (next ?? [])
                          .map((x: { label?: string; value?: string }) => norm(x?.label ?? x?.value ?? ''))
                          .filter(Boolean)
                      ));
                      addCoAuthorRespondentOptions(names.map((name) => ({ value: name, label: name })));

                      setFields((p) =>
                        p.map((x) => (x.id === f.id ? { ...x, value: names } : x))
                      );
                    }}
                    searchPlaceholder="検索 / 入力で追加"
                    maxSelected={TASK_MEMBERS_MAX_SELECTED}
                    maxSelectedMessage={TASK_MEMBERS_MAX_SELECTED_MESSAGE}
                    allowCustomValue
                  />
                );
              })()
            ) : f.type === '開始終了日付' ? (
              <WeeklyDatePicker
                start={startDate}
                end={endDate}
                onChange={(range) => {
                  // state を正とする（＝ここがUIの真実）
                  const from = range?.from || '';
                  const to = range?.to || '';
                  setStartDate(from);
                  setEndDate(to);
                  onDateRangeChange?.({
                    from,
                    to,
                  });

                  // fields 側への反映は「期間の同期 useEffect」でやるので、
                  // ここでは setFields は呼ばない（片方向同期にする）
                }}
                enlightSyncEnabled={
                  enlightSyncEnabled
                }
              />
          ) : (
            <>
              {renderDynamic(f)}
              <RegisteredInfo />
            </>
          )}
        </div>

        {f.isRemovable && (
          <button
            className="delete-button"
            type="button"
            onClick={() =>
              setFields((p) =>
                p.filter((x) => x.id !== f.id)
              )
            }
          />
        )}
      </div>
    );
  };

  /* ---------- マスターの options 同期（動的属性も一括） ---------- */
  useEffect(() => {
    setFields((prev) => {
      let changed = false;
      const next = prev.map((f) => {
        if (
          f.type === '記入者' &&
          !sameOptions(f.options, reporterOpts)
        ) {
          changed = true;
          return { ...f, options: reporterOpts };
        }
        if (
          f.type === '承認AM' &&
          !sameOptions(f.options, approverAMOpts)
        ) {
          changed = true;
          return {
            ...f,
            options: approverAMOpts,
          };
        }

        if (f.type === 'チーム') {
          const teamAsOptions = uniqueOptions(
            teamOpts
          );
          if (
            !sameOptions(
              f.options,
              teamAsOptions
            )
          ) {
            changed = true;
            return {
              ...f,
              options: teamAsOptions,
            };
          }
        }

        const meta = effectiveMetaByName.get(
          f.type
        );
        let base = f;
        if (meta && !FIXED_BASE_NAMES.has(f.type)) {
          const nextIsRequired =
            !f.isRemovable &&
            Number((meta as any).input_required ?? 0) === 1;
          const nextAttrId = Number(meta.attr_id);
          if (
            f.isRequired !== nextIsRequired ||
            f.attr_id !== nextAttrId
          ) {
            changed = true;
            base = {
              ...f,
              isRequired: nextIsRequired,
              attr_id: nextAttrId,
            };
          }
        }
        if (meta && isSelectType(meta.attr_type)) {
          const bucketOpts = optionsFromBuckets(
            master,
            meta.attr_name,
            Number(meta.attr_id),
            currentDeptId || 0,
            currentTeamId || 0
          );
          const nextOpts = bucketOpts.length
            ? bucketOpts
            : normalizeOptions(meta.options);
          const need =
            !sameOptions(f.options, nextOpts) ||
            f.attr_id !== Number(meta.attr_id);

          if (need) {
            // チームが変わったら無条件で値クリア
            if (f.type === CLASSIFICATION_MARKET_TARGET_ATTR_NAME) {
              const teamChanged =
                !!prevTeamRef.current &&
                prevTeamRef.current !== team;

              if (teamChanged) {
                const isMulti =
                  toMultiFlag(
                    meta?.multiple_select
                  ) === 1;
                const def = isMulti
                  ? []
                  : {
                      value: '',
                      label: '',
                    };
                setReportType('');
                changed = true;
                return {
                  ...base,
                  options: nextOpts,
                  attr_id: Number(meta.attr_id),
                  value: def,
                };
              } else {
                // より厳密な値の存在チェック（value/label が nextOpts に実在するか）
                const existsInOpts = (v: any) => {
                  if (Array.isArray(v)) {
                    return v.every((x) => {
                      const val = String(
                        x?.value ?? ''
                      );
                      const lbl = String(
                        x?.label ?? ''
                      );
                      return nextOpts.some(
                        (o) =>
                          o.value === val &&
                          o.label === lbl
                      );
                    });
                  }
                  if (
                    v &&
                    typeof v === 'object'
                  ) {
                    const val = String(
                      (v as any)?.value ?? ''
                    );
                    const lbl = String(
                      (v as any)?.label ?? ''
                    );
                    return nextOpts.some(
                      (o) =>
                        o.value === val &&
                        o.label === lbl
                    );
                  }
                  return v === ''; // 空はOK（＝未選択）
                };

                let nextValue: any = f.value;

                // 週報分類は「常に配列」運用 + options が変わったら label→value(ID) に寄せる
                const canon = sanitizeSelectArrayByOptions(
                  asArrayValue(f.value),
                  nextOpts,
                  false
                );

                // canon が空なら未選択へ
                if (canon.length === 0) {
                  nextValue = [];
                  setReportType('');
                } else {
                  nextValue = canon;
                  setReportType(canon.map((x) => x.label).filter(Boolean).join(','));
                }

                changed = true;
                return {
                  ...base,
                  options: nextOpts,
                  attr_id: Number(meta.attr_id),
                  value: nextValue,
                };
              }
            }

            // その他の動的属性は通常どおり options/attr_id を同期
            changed = true;
            return {
              ...base,
              options: nextOpts,
              attr_id: Number(meta.attr_id),
            };
          }
        }
        return base;
      });
      return changed ? next : prev;
    });
  }, [
    reporterOpts,
    approverAMOpts,
    effectiveMetaByName,
    master,
    teamOpts,
    currentDeptId,
    currentTeamId,
    team, // 念のため追加
  ]);

  /* ---------- division 変更で値リセット ---------- */
  useEffect(() => {
    const div = division;
    setFields((prev) => {
      let changed = false;
      const next = prev.map((f) => {
        if (f.divisionContext === div) return f;
        changed = true;
        const meta = effectiveMetaByName.get(f.type);
        const def = meta
          ? defaultValueForAttr(
              meta.attr_type,
              meta.multiple_select
            )
          : f.type === '連名者'
          ? []
          : f.type === '承認AM' ||
            f.type === '記入者'
          ? ''
          : f.type === '開始終了日付'
          ? { from: '', to: '' }
          : f.value;
        return {
          ...f,
          divisionContext: div,
          value: def,
        };
      });
      return changed ? next : prev;
    });
  }, [division, effectiveMetaByName]);

  /* ---------- スナップショット通知（承認-3は通知から除外） ---------- */
  useEffect(() => {
    const activeDynamicNameSet = new Set(
      allDynamicVisibleSorted.map((a) => normalizeManagedAttrNameKey(a.attr_name))
    );
    const snapshot = fields.filter(
      (f) =>
        f.type !== '承認-3' &&
        (FIXED_BASE_NAMES.has(f.type) ||
          activeDynamicNameSet.has(normalizeManagedAttrNameKey(f.type)))
    );
    fieldsSnapshotRef.current?.(snapshot);
  }, [fields, allDynamicVisibleSorted]);

  /**
   * チーム変更時に自動クリアしたい項目名のセット
   *
   * クリア対象にしたいもの:
   * - チーム単位で管理されている属性（team_id > 0 の属性や、bucket が team_id を持つもの）
   * - 「週報分類」（特別扱いで常にクリア）
   * - フリーワード系（attr_required === 0 の自由入力欄はチーム跨ぎで意味が変わりやすいのでクリア）
   * - 「フリー」も毎回クリアしたいという要件
   */
  const clearOnTeamChangeSet = useMemo(() => {
    const dynamicNames = new Set<string>();

    // 1. attributeManage の team_id>0 はチーム依存としてクリア対象
    (allAttributes ?? []).forEach((attr) => {
      const tId = Number(attr.team_id);
      if (Number.isFinite(tId) && tId > 0) {
        const nm = attr.attr_name;
        if (nm && !FIXED_BASE_NAMES.has(nm)) {
          dynamicNames.add(nm);
        }
      }
    });

    // 2. attributeBuckets の中で team_id が付いてるものもクリア対象
    const buckets = (master as any)
      ?.attributeBuckets as
      | Array<{ name: string; items: any[] }>
      | undefined;

    if (Array.isArray(buckets)) {
      buckets.forEach((bucket) => {
        const n = bucket?.name;
        if (!n || FIXED_BASE_NAMES.has(n)) return;

        const teamScoped = (bucket.items ?? []).some(
          (it: any) =>
            Number(it?.team_id ?? it?.teamId) > 0
        );
        if (teamScoped) {
          dynamicNames.add(String(n));
        }
      });
    }

    // 3. attr_required === 0 の自由入力系もクリア対象
    (allAttributes ?? []).forEach((attr) => {
      if (isFreeInputManagedAttr(attr)) {
        const nm = attr.attr_name;
        if (nm && !FIXED_BASE_NAMES.has(nm)) {
          dynamicNames.add(nm);
        }
      }
    });

    // 4. 特別扱いの項目
    dynamicNames.add(CLASSIFICATION_MARKET_TARGET_ATTR_NAME);
    COMMON_FREE_TYPE_NAMES.forEach((name) => dynamicNames.add(name));
    dynamicNames.add(FIXED_ATTR_NAME_PROJECT); // （チーム跨ぎで持ち越さない）

    return dynamicNames;
  }, [allAttributes, master]);

  // そのチームに初めて来たときは attribute_values を反映させない
  // ただし 2回目以降に戻ってきたチームは反映OKにする
  const visitedTeamsRef = useRef<Set<string>>(new Set());
  const blockBackfillTeamsRef = useRef<Set<string>>(new Set());

  /* ---------- 親 → 固定フィールド反映 ---------- */
  useEffect(() => {
    setFields((prev) =>
      prev.map((f) => {
        if (f.type === '記入者' && !f.isRemovable) {
          const nextVal = reporter || '';
          return f.value === nextVal
            ? f
            : { ...f, value: nextVal };
        }
        if (f.type === 'チーム' && !f.isRemovable) {
          const curVal =
            typeof f.value === 'object'
              ? (f.value as any)?.value
              : f.value;
          if (curVal === team) return f;
          const lbl =
            uniqueOptions(teamOpts).find(
              (o) => o.value === team
            )?.label ?? team;
          return {
            ...f,
            value: { value: team, label: lbl },
            options: uniqueOptions(teamOpts),
          };
        }
        if (f.type === '承認AM' && !f.isRemovable) {
          const curVal =
            typeof f.value === 'object'
              ? (f.value as any)?.value
              : f.value;
          if (curVal === approverAM) return f;
          const lbl =
            uniqueOptions(approverAMOpts).find(
              (o) => o.value === approverAM
            )?.label ?? approverAM;
          return approverAM
            ? {
                ...f,
                value: {
                  value: approverAM,
                  label: lbl,
                },
                options: uniqueOptions(
                  approverAMOpts
                ),
              }
            : {
                ...f,
                value: '',
                options: uniqueOptions(
                  approverAMOpts
                ),
              };
        }
        return f;
      })
    );
  }, [reporter, team, teamOpts, approverAM, approverAMOpts]);

  // 直前の課
  useEffect(() => {
    const deptNow = currentDeptId ?? null;

    // 直前と同じ課なら何もしない
    if (prevDeptRef.current === deptNow) return;

    // 課が変わったタイミング
    prevDeptRef.current = deptNow;
   // この課での「最初のチーム」はまだ確定していないので、いったん未設定にする
   // （本当に最初に表示されたチームは、後述のチーム変更useEffect側で確定させる）
   originalTeamRef.current = '';

    // チーム訪問済みフラグをリセット（課をまたいだら別物と考える）
    visitedTeamsRef.current = new Set<string>();

    // 「このチームは最初だけバックフィル止める」フラグもリセット
    blockBackfillTeamsRef.current = new Set<string>();

    // スナップショットも課ごとに独立させたいのでクリア
    teamSnapshotsRef.current = new Map<string, {
      approver3: typeof approver3;
      approverAM: typeof approverAM;
      startDate: typeof startDate;
      endDate: typeof endDate;
      reportType: typeof reportType;
      project: string;
      taskMembers: any[];
    }>();

    // 課が変わったので、前チームは無しからスタート
    prevTeamRef.current = '';

    // これで、
    // - deptを切り替えたあと最初に見えるチームは「オリジナル扱い」
    // - A→B→A と戻ったとき A は isOriginalTeam=true になる
    // - blockBackfillNow が false になって DBの値で再復元できる
  }, [currentDeptId, team, approver3, approverAM, startDate, endDate, reportType]);


  /* ---------- 期間の同期 ---------- */
  /* 
   WeeklyDatePicker は startDate/endDate は、
   ここは state -> fields の一方向同期（ミラー更新）のみを担当する。
   fields 側は画面表示のソースではなく、検索ペイロード用の保持領域になる。
  */
  useEffect(() => {
    setFields((prev) =>
      prev.map((f) => {
        if (
          f.type === '開始終了日付' &&
          !f.isRemovable
        ) {
          const nextVal = {
            from: startDate || '',
            to: endDate || '',
          };
          if (
            f?.value?.from === nextVal.from &&
            f?.value?.to === nextVal.to
          )
            return f;
          return { ...f, value: nextVal };
        }
        return f;
      })
    );
  }, [startDate, endDate]);

  /* ---------- パワートレインの state を fields から同期 ---------- */
  useEffect(() => {
    const base = fields.find(
      (f) =>
        f.type === FIXED_ATTR_NAME_POWERTRAIN && !f.isRemovable
    );
    const next = base ? pickValueLike(base.value) : '';
    if (next !== powertrain) setPowertrain(next);
  }, [fields, powertrain]);

  /* ---------- Project の state を fields から同期（表示用） ---------- */
  useEffect(() => {
    const base = fields.find(
      (f) =>
        f.type === FIXED_ATTR_NAME_PROJECT && !f.isRemovable
    );
    const next = base ? pickValueLike(base.value) : '';
    if (next !== project) setProject(next);
  }, [fields, project]);

  /* ---------- 週報分類の state を fields から同期（表示用） ---------- */
  useEffect(() => {
    const base = fields.find(
      (f) =>
        f.type === CLASSIFICATION_MARKET_TARGET_ATTR_NAME && !f.isRemovable
    );
    const next = base ? pickValueLike(base.value) : '';
    if (next !== reportType) setReportType(next);
  }, [fields, reportType]);

  useEffect(() => {
    const base = fields.find(
      (f) =>
        f.type === FIXED_ATTR_NAME_SHIMUKE && !f.isRemovable
    );
    const next = base ? pickValueLike(base.value) : '';
    if (next !== shikimuke) setShikimuke(next);
  }, [fields, shikimuke]);

  useEffect(() => {
    const base = fields.find(
      (f) =>
        f.type === FIXED_ATTR_NAME_LOT && !f.isRemovable
    );
    const next = base ? pickValueLike(base.value) : '';
    if (next !== lot) setLot(next);
  }, [fields, lot]);


  /* ---------- チーム変更検知＆クリア/復元処理 ---------- */
  /*
    ユーザーがチームを変更した時に発火。
    変更直前のチーム(A)の状態(承認AMなど)を teamSnapshotsRef に保存。
    → Bチームへ切り替え
      - そのBチームを過去に表示したことがあれば、そのスナップショットを復元する
      - 初めてなら既存ロジックどおりにクリア
    これで「A→B→Aで戻った時にAの承認AMがBの値になる」問題を防ぐ。
  */
  useEffect(() => {
    if (prevTeamRef.current === team) return;

    resetTouchedTypes(); // チームが変わったら touched はクリア（新チームで再編集可能に）

    const oldTeam = prevTeamRef.current || '';
    const newTeam = team;
    const isUserChange = userTouchedTeamRef.current === true;

    // ここがSTART
    {
      // 課切り替え直後に、古い課のteam_idを
      // originalTeamRef.current に入れないようにする
      const teamsArr: any[] = Array.isArray((master as any)?.teams)
        ? (master as any).teams
        : [];

      const hitTeamForNew = teamsArr.find(
        (t: any) => String(t?.team_id ?? t?.id) === String(newTeam),
      );

      const newTeamDeptId = Number(hitTeamForNew?.department_id) || 0;
      const sameDeptAsCurrent = newTeamDeptId === (currentDeptId || 0);

      if (
        originalTeamRef.current === '' &&
        newTeam &&
        sameDeptAsCurrent
      ) {
        originalTeamRef.current = newTeam;
      }
    }

    // まず、いままでのチーム(oldTeam)の状態を保存しておく
    if (oldTeam) {
      const currentTaskMembersField = fields.find(
        (f) => f.type === '連名者' && !f.isRemovable,
      );

      const currentTaskMembers = Array.isArray(currentTaskMembersField?.value)
        ? limitTaskMembers(currentTaskMembersField!.value)
        : [];

      teamSnapshotsRef.current.set(oldTeam, {
        approver3,
        approverAM,
        startDate,
        endDate,
        reportType,
        project,
        taskMembers: currentTaskMembers,
      });
    }

    // prevTeamRef を更新（次回比較用）
    prevTeamRef.current = newTeam;

    // 自動プレフィルによる変更（ユーザー操作じゃない）はクリア/復元処理しない
    if (!isUserChange) return;

    // 訪問済み管理（初回チームでは attribute_values バックフィルを1回ブロックする運用）
    if (newTeam) {
      const key = String(newTeam);

      if (!visitedTeamsRef.current.has(key)) {
        visitedTeamsRef.current.add(key);

        const isOriginalTeam =
          String(key) === String(originalTeamRef.current ?? '');

        if (!isOriginalTeam) {
          blockBackfillTeamsRef.current.add(key);
        }
      }
    }

    // 次のチーム(newTeam)に対して、過去に保存したスナップショットがあればそれを復元
    const cached = newTeam
      ? teamSnapshotsRef.current.get(String(newTeam))
      : undefined;

    if (cached) {
      // 復元クリアするのではなく、cached の値を元に戻す
      setApprover3(cached.approver3 || '');
      onApprover3Change?.(cached.approver3 || '');

      setApproverAM(cached.approverAM || '');
      onApproverAMChange?.(cached.approverAM || '');
      setProject(cached.project || '');

      setStartDate(cached.startDate || '');
      setEndDate(cached.endDate || '');
      onDateRangeChange?.({
        from: cached.startDate || '',
        to: cached.endDate || '',
      });

      setReportType(cached.reportType || '');

      setFields((prev) => {
        let changed = false;

        const next = prev.map((f) => {
          if (f.isRemovable) return f;

          // 承認AM の復元
          if (f.type === '承認AM') {
            const v = cached.approverAM || '';
            const cur = pickValueLike(f.value);
            if (cur === v) return f;

            changed = true;

            if (v) {
              const lbl =
                uniqueOptions(approverAMOpts).find((o) => o.value === v)?.label ??
                v;

              return {
                ...f,
                value: {
                  value: v,
                  label: lbl,
                },
              };
            } else {
              return { ...f, value: '' };
            }
          }

          // 連名者の復元
          if (f.type === '連名者') {
            const prevArr = Array.isArray(f.value) ? f.value : [];
            const restoreArr = Array.isArray(cached.taskMembers)
              ? limitTaskMembers(cached.taskMembers)
              : [];

            const sameLen = prevArr.length === restoreArr.length;
            const sameAll =
              sameLen && prevArr.every((v, i) => v === restoreArr[i]);

            if (!sameAll) {
              changed = true;
              return {
                ...f,
                value: restoreArr,
              };
            }

            return f;
          }

          // 日付レンジの復元
          if (f.type === '開始終了日付') {
            const restoredRange = {
              from: cached.startDate || '',
              to: cached.endDate || '',
            };

            const curFrom = (f as any)?.value?.from || '';
            const curTo = (f as any)?.value?.to || '';

            if (curFrom === restoredRange.from && curTo === restoredRange.to) {
              return f;
            }

            changed = true;

            return {
              ...f,
              value: restoredRange,
            };
          }

          // 週報分類についてもできれば復元したいが、
          // チーム依存で候補が変わるので、候補と突き合わせて再マッピングする
          if (f.type === CLASSIFICATION_MARKET_TARGET_ATTR_NAME) {
            const meta = effectiveMetaByName.get(CLASSIFICATION_MARKET_TARGET_ATTR_NAME);

            const opts = optionsFromBuckets(
              master,
              meta?.attr_name,
              Number(meta?.attr_id),
              currentDeptId || 0,
              currentTeamId || 0,
            );

            const isMulti = toMultiFlag(meta?.multiple_select) === 1;
            const optIndex = buildOptionIndex(uniqueOptions(opts));

            if (cached.reportType) {
              const labelsWanted = cached.reportType
                .split(',')
                .map((s) => cleanupToken(s))
                .filter(Boolean);

              let newValLocal: any = f.value;

              if (isMulti) {
                const mapped = labelsWanted.map((txt) => {
                  const hit = pickOptionPreferLabel(txt, optIndex);

                  return hit
                    ? {
                        value: hit.value,
                        label: hit.label,
                      } // value=ID,label=表示
                    : {
                        value: norm(txt),
                        label: norm(txt),
                      }; // 候補外は生文字
                });

                newValLocal = mapped;
              } else {
                const baseLbl = labelsWanted[0] || '';

                if (baseLbl) {
                  const hit = pickOptionPreferLabel(baseLbl, optIndex);

                  newValLocal = hit
                    ? {
                        value: hit.value,
                        label: hit.label,
                      }
                    : {
                        value: norm(baseLbl),
                        label: norm(baseLbl),
                      };
                } else {
                  newValLocal = {
                    value: '',
                    label: '',
                  };
                }
              }

              const curStr = pickValueLike(f.value);
              const nextStr = pickValueLike(newValLocal);

              if (curStr !== nextStr) {
                changed = true;

                return {
                  ...f,
                  value: newValLocal,
                  options: uniqueOptions(opts),
                };
              }
            }
          }

          return f;
        });

        return changed ? next : prev;
      });

      return; // 復元ブランチ終了
    }

    // ---- キャッシュが無い場合（新しいチームに初めて入った場合）----

    // 承認-3
    setApprover3('');
    onApprover3Change?.('');

    // 承認AM
    setApproverAM('');
    onApproverAMChange?.('');

    // 日付
    setStartDate('');
    setEndDate('');
    onDateRangeChange?.({ from: '', to: '' });

    // 週報分類などの表示用 state
    setReportType('');
    setProject('');

    // --- fields も整合性のためにクリアする ---
    setFields((prev) => {
      let changed = false;

      const next = prev.map((f) => {
        if (f.isRemovable) return f;

        // 1) 週報分類は候補を現在のチーム/課に合わせて差し替えて空に
        if (f.type === CLASSIFICATION_MARKET_TARGET_ATTR_NAME) {
          const meta = effectiveMetaByName.get(CLASSIFICATION_MARKET_TARGET_ATTR_NAME);

          const opts = optionsFromBuckets(
            master,
            meta?.attr_name,
            Number(meta?.attr_id),
            currentDeptId || 0,
            currentTeamId || 0,
          );

          const def: any = [];

          changed = true;

          return {
            ...f,
            value: def,
            options: uniqueOptions(opts),
          };
        }

        // 2) 日付レンジを初期化
        if (f.type === '開始終了日付') {
          const clearedRange = {
            from: '',
            to: '',
          };

          const curFrom = (f as any)?.value?.from || '';
          const curTo = (f as any)?.value?.to || '';

          if (curFrom === '' && curTo === '') {
            // もともと空ならそのまま
            return f;
          }

          changed = true;

          return {
            ...f,
            value: clearedRange,
          };
        }

        // 3) 承認AM を初期化
        if (f.type === '承認AM') {
          const cur = pickValueLike(f.value);

          if (cur === '') {
            return f;
          }

          changed = true;

          return { ...f, value: '' };
        }

        // 4) 連名者ー を初期化
        if (f.type === '連名者') {
          const curArr = Array.isArray(f.value) ? f.value : [];

          if (curArr.length === 0) {
            // もともと空ならそのまま
            return f;
          }

          changed = true;

          return {
            ...f,
            value: [], // 課題メンバーはチームをまたいだら空にする
          };
        }

        // 5) Project も同じく候補を差し替えて空にする
        if (f.type === FIXED_ATTR_NAME_PROJECT) {
          const meta = effectiveMetaByName.get(FIXED_ATTR_NAME_PROJECT);

          const opts = optionsFromBuckets(
            master,
            meta?.attr_name,
            Number(meta?.attr_id),
            currentDeptId || 0,
            currentTeamId || 0,
          );

          const def: any = [];

          changed = true;

          return {
            ...f,
            value: def,
            options: uniqueOptions(opts),
          };
        }

        // 6) その他、clearOnTeamChangeSet 対象の項目をデフォ値に戻す
        if (!clearOnTeamChangeSet.has(f.type)) {
          return f; // クリア対象じゃない
        }

        const meta = effectiveMetaByName.get(f.type);

        const def = meta
          ? defaultValueForAttr(
              meta.attr_type,
              meta.multiple_select,
            )
          : '';

        const curS = pickValueLike(f.value);
        const defS = pickValueLike(def);

        if (curS === defS) {
          return f;
        }

        changed = true;

        return { ...f, value: def };
      });

      return changed ? next : prev;
    });
    // 復元 or クリア処理ここまで
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    team,
    approverAM,
    approver3,
    startDate,
    endDate,
    reportType,
    effectiveMetaByName,
    master,
    currentDeptId,
    currentTeamId,
    clearOnTeamChangeSet,
    approverAMOpts,
    onApprover3Change,
    onApproverAMChange,
    onDateRangeChange,
  ]);


  /* ---------- DB 上の動的属性を自動反映 ---------- */
  useEffect(() => {
    if (!prefill?.attribute_values) return;
    setFields((prev) => {
      let next = [...prev];

      const visibleNameSet = new Set(
        allDynamicVisibleSorted.map(
          (a) => norm(a.attr_name)
        )
      );
      next = next.filter((f) => {
        if (FIXED_BASE_NAMES.has(f.type)) return true;
        return visibleNameSet.has(norm(f.type));
      });

      let changed = false;
      allDynamicVisibleSorted.forEach((attr) => {
        const exists = next.some(
          (f) =>
            sameName(
              f.type,
              attr.attr_name
            ) && !f.isRemovable
        );
        const optsFromBuckets = optionsFromBuckets(
          master,
          attr.attr_name,
          Number(attr.attr_id),
          currentDeptId || 0,
          currentTeamId || 0
        );
        const opts = optsFromBuckets.length
          ? optsFromBuckets
          : normalizeOptions(attr.options);
        if (!exists) {
          const defaultVal = defaultValueForAttr(
            attr.attr_type,
            attr.multiple_select
          );

          next.push({
            id: `base-${slugify(
              attr.attr_name
            )}-${attr.attr_id}`,
            type: attr.attr_name,
            label: attr.attr_name,
            value: defaultVal,
            isRemovable: false,
            isRequired: Number((attr as any).input_required ?? 0) === 1,
            divisionContext: division,
            options: isSelectType(
              attr.attr_type
            )
              ? uniqueOptions(opts)
              : undefined,
            attr_id: Number(attr.attr_id),
          });
          changed = true;
        } else {
          const attrId = Number(attr.attr_id);
          const isRequired = Number((attr as any).input_required ?? 0) === 1;
          const nextOptions = isSelectType(attr.attr_type)
            ? uniqueOptions(opts)
            : undefined;

          next = next.map((f) => {
            if (f.isRemovable) return f;
            if (!sameName(f.type, attr.attr_name)) return f;

            const nextId = String(f.id ?? '').startsWith('base-')
              ? `base-${slugify(attr.attr_name)}-${attr.attr_id}`
              : f.id;
            const optionsChanged =
              JSON.stringify(f.options ?? null) !==
              JSON.stringify(nextOptions ?? null);
            const needsUpdate =
              f.attr_id !== attrId ||
              f.type !== attr.attr_name ||
              f.label !== attr.attr_name ||
              f.isRequired !== isRequired ||
              f.divisionContext !== division ||
              f.id !== nextId ||
              optionsChanged;

            if (!needsUpdate) return f;
            changed = true;
            return {
              ...f,
              id: nextId,
              type: attr.attr_name,
              label: attr.attr_name,
              isRequired,
              divisionContext: division,
              options: nextOptions,
              attr_id: attrId,
            };
          });
        }
      });

      const unchanged =
        !changed &&
        next.length === prev.length &&
        next.every(
          (it, i) => it === prev[i]
        );

      return unchanged ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    reporterOpts,
    approverAMOpts,
    effectiveMetaByName,
    master,
    teamOpts,
    currentDeptId,
    currentTeamId,
    division,
  ]);

  // 動的ベースの用意完了判定
  const dynamicBaseReady = useMemo(() => {
    const required = new Set(
      allDynamicVisibleSorted.map(
        (a) => a.attr_name
      )
    );
    const present = fields.filter(
      (f) =>
        !f.isRemovable &&
        required.has(f.type)
    ).length;
    return present >= required.size;
  }, [fields, allDynamicVisibleSorted]);

  /* ---------- バックエンド保存用属性の通知 ---------- */
  useEffect(() => {
    if (!attributesChangeRef.current) return;

    const out: Array<{
      attr_id: number;
      attr_name: string;
      attr_value: string;
    }> = [];

    fields.forEach((f) => {
      if (FIXED_BASE_NAMES.has(f.type)) return;

      const meta = resolveScopedAttrMeta(f.type, f.attr_id);
      const id = toNumberOrNullLocal(meta?.attr_id);
      if (id == null) return;

      const val = pickValueLike(f.value);
      if (!val) return;

      out.push({
        attr_id: id,
        attr_name: meta?.attr_name ?? f.type,
        attr_value: val,
      });
    });

    attributesChangeRef.current(out);
  }, [fields, resolveScopedAttrMeta]);

  /* ---------- attribute_values → 動的属性へ適用 ---------- */
  useEffect(() => {
    const prefillAttrValues = getPrefillAttributeValues(prefill);

    if (prefillAttrValues.length === 0) return;
    if (!dynamicBaseReady) return;

    // 初期チームかどうか。初期チームではブロックしない。
    const isOriginalTeam =
      originalTeamRef.current === '' ||
      team === originalTeamRef.current;

    // このチームは最初の1回だけでフラグ、かつ初期チームでないときだけ有効化
    const blockBackfillNow =
      !isOriginalTeam &&
      blockBackfillTeamsRef.current.has(String(team));

    // 取り込みを堅牢化：配列/JSON/typo を許容して Map<attr_id, string[]> に正規化
    // 重要: attr_name が無い { attr_id: 145, attr_value: '共通' } でも、
    // 145 のマスタ名から現在スコープの 5=仕向け へ remap する。
    const parseAttrValuesToMap = (): Map<number, string[]> => {
      const out = new Map<number, string[]>();

      for (const item of prefillAttrValues) {
        if (item == null) continue;

        const rawId = toNumberOrNullLocal(
          (item as any).attr_id ??
            (item as any).attri_id ?? // タイポ救済
            (item as any).attribute_id ??
            (item as any).id
        );
        const sourceMeta = rawId != null ? attrMetaById.get(rawId) : undefined;

        const nameRaw =
          (item as any).attr_name ??
          (item as any).attrName ??
          (item as any).attribute_name ??
          (item as any).attributeName ??
          (item as any).name ??
          (item as any).label ??
          sourceMeta?.attr_name;

        const resolvedMeta = resolveScopedAttrMeta(nameRaw, rawId);
        const id = toNumberOrNullLocal(resolvedMeta?.attr_id);

        const valRaw =
          (item as any).attr_value ??
          (item as any).attrValue ??
          (item as any).value ??
          (item as any).text ??
          '';

        const val = keepRawToken(valRaw);
        if (id == null || val === '') continue;

        const box = out.get(id) ?? [];
        box.push(val);
        out.set(id, box);
      }

      return out;
    };

    const attrIdToValues = parseAttrValuesToMap();

    // f がメタ情報のデフォルト値のままかどうか
    const isDefault = (f: DynamicFieldItem, meta?: AttrFromMaster) => {
      if (!meta) return false;
      const def = defaultValueForAttr(meta.attr_type, meta.multiple_select);

      // デフォ判定は従来どおりでOK（空かどうか判定が主用途）
      return pickValueLike(f.value) === pickValueLike(def);
    };

    setFields((prev) => {
      let changed = false;

      const next: DynamicFieldItem[] = prev.map((f) => {
        // 固定項目は対象外
        if (FIXED_BASE_NAMES.has(f.type)) return f;

        // meta を確定。f.attr_id が別チーム由来なら、名前から現在スコープの attr_id に補正する。
        const meta = resolveScopedAttrMeta(f.type, f.attr_id);
        if (!meta) return f;

        // ユーザーが触ったフィールドはDB(attribute_values)で上書きしない（空欄も尊重）
        if (touchedTypesRef.current.has(f.type)) return f;

        // チーム依存項目は、切替直後の blockBackfillNow 中は絶対に上書きしない
        if (clearOnTeamChangeSet.has(f.type) && blockBackfillNow) return f;

        // ユーザーが手動でチームを変えた後は、
        // クリア対象かつ今もデフォルト値のフィールドだけDB値を復元可
        if (userTouchedTeamRef.current) {
          if (!clearOnTeamChangeSet.has(f.type) || !isDefault(f, meta)) {
            return f;
          }
        }

        const id = Number(meta.attr_id);

        const t = toAttrTypeCode(meta.attr_type);

        // meta上の複数フラグ
        const isMultiMeta = toMultiFlag(meta.multiple_select) === 1;

        // 週報分類は DB が "内報 / 外報 / 内々報" のような複数を1文字列で返すので、常に multi 扱い
        const isMulti11 = t === 11 && (isMultiMeta || meta.attr_name === CLASSIFICATION_MARKET_TARGET_ATTR_NAME);

        const isSelectLike = [11, 12, 13].includes(t);

        // ---- DB値取り込み ----
        const sourceValsRaw: string[] = (attrIdToValues.get(id) ?? [])
          .map(keepRawToken)
          .filter((v) => v !== '');

        // 週報分類は、正式名称に含まれる "/" と複数値の区切りを区別するため、
        // 分割前に現在の課・チームのマスタ候補を確定する。
        const bucketOpts = isSelectLike
          ? optionsFromBuckets(
              master,
              meta.attr_name,
              id,
              currentDeptId || 0,
              currentTeamId || 0
            )
          : [];
        const opts = isSelectLike
          ? uniqueOptions(
              (bucketOpts.length ? bucketOpts : normalizeOptions(meta.options)).map(toOption)
            )
          : [];

        let rawList: string[] = [];

        if (isSelectLike) {
          // セレクト系はマッチングしやすいように末尾の「です。」等だけ落とす
          if (t === 11 && !isMulti11) {
            // 単一selectは分割しない（元データを尊重）
            rawList = sourceValsRaw.map((v) => cleanupToken(v)).filter(Boolean);
          } else if (meta.attr_name === CLASSIFICATION_MARKET_TARGET_ATTR_NAME) {
            const knownReportTypeValues = opts.flatMap((option) => [
              option.label,
              option.value,
            ]);
            rawList = sourceValsRaw
              .flatMap((value) =>
                resolveKnownMultiSelectTokens(value, knownReportTypeValues, {
                  onlyKnown: true,
                })
              )
              .map(cleanupToken)
              .filter(Boolean);
          } else {
            rawList = sourceValsRaw
              .flatMap((v) => v.split(SPLIT_MULTI_SELECT))
              .map(cleanupToken)
              .filter(Boolean);
          }
        } else {
          // 自由入力：全部保持
          const joined = sourceValsRaw.join('\n');
          rawList = joined !== '' ? [joined] : [];
        }

        // 現在値の比較（自由入力は絶対に trim しない）
        const curComparable = isSelectLike ? pickValueLike(f.value) : keepRawToken(f.value);

        // 値が全く無い場合はデフォルトへ
        if (rawList.length === 0) {
          const def = defaultValueForAttr(meta.attr_type, meta.multiple_select);
          const defComparable = isSelectLike ? pickValueLike(def) : keepRawToken(def);

          if (defComparable === curComparable) return f;

          changed = true;
          return {
            ...f,
            value: def,
            attr_id: id,
          };
        }

        // ---- 自由入力（text/textarea/int/decimal/date/time）----
        if (!isSelectLike) {
          const nextValRaw = isMulti11 ? rawList.join('\n') : rawList[0];
          if (nextValRaw === curComparable) return f;

          changed = true;
          return {
            ...f,
            value: nextValRaw,
            attr_id: id,
          };
        }

        // ---- セレクト系（11/12/13）----
        const fallbackOptsFromRaw = uniqueOptions(
          rawList.map((lbl) => ({ value: norm(lbl), label: norm(lbl) }))
        );

        const usedOpts = opts.length ? opts : fallbackOptsFromRaw;
        const optIndex = buildOptionIndex(usedOpts);

        const curValStr = curComparable; // selectLike なので pickValueLike 済み
        let newVal: any = f.value;

        // 11: select
        if (t === 11) {
          if (isMulti11) {
            const mapped = sanitizeSelectArrayByOptions(
              rawList.map((txt) => {
                const hit = pickOptionPreferLabel(txt, optIndex);
                return hit
                  ? { value: hit.value, label: hit.label }
                  : { value: norm(txt), label: norm(txt) };
              }),
              usedOpts,
              meta.attr_name !== CLASSIFICATION_MARKET_TARGET_ATTR_NAME
            );

            const nextStr = pickValueLike(mapped);
            if (nextStr !== curValStr) {
              newVal = mapped;
              changed = true;
            }

            return newVal === f.value
              ? f
              : { ...f, value: newVal, options: usedOpts, attr_id: id };
          }

          const base = rawList[0];
          const hit = base ? pickOptionPreferLabel(base, optIndex) : null;

          const nextVal = hit
            ? { value: hit.value, label: hit.label }
            : { value: norm(base ?? ''), label: norm(base ?? '') };

          const nextStr = pickValueLike(nextVal);
          if (nextStr !== curValStr) {
            newVal = nextVal;
            changed = true;
          }

          return newVal === f.value
            ? f
            : { ...f, value: newVal, options: usedOpts, attr_id: id };
        }

        // 12: checkbox（値は “ラベルの配列” を保持）
        if (t === 12) {
          const mappedLabels = rawList
            .map((txt) => pickOptionPreferLabel(txt, optIndex)?.label ?? cleanupToken(txt))
            .filter(Boolean);

          const nextStr = mappedLabels.join(',');
          if (nextStr !== curValStr) {
            newVal = mappedLabels;
            changed = true;
          }

          return newVal === f.value
            ? f
            : { ...f, value: newVal, attr_id: id, options: usedOpts };
        }

        // 13: datalist（単一扱い・複数はカンマ結合）
        if (t === 13) {
          const one = isMultiMeta ? rawList.join(',') : rawList[0];

          const hit = one ? pickOptionPreferLabel(one, optIndex) : null;

          const nextVal = hit
            ? { value: hit.value, label: hit.label }
            : cleanupToken(one ?? '');

          const nextStr = pickValueLike(nextVal);
          if (nextStr !== curValStr) {
            newVal = nextVal;
            changed = true;
          }

          return newVal === f.value
            ? f
            : { ...f, value: newVal, attr_id: id, options: usedOpts };
        }

        return f;
      });

      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    prefill?.attribute_values,
    prefill?.attributes,
    prefill?.attributeValues,
    prefill?.base_attributes,
    prefill?.baseAttributes,
    prefill?.weekly_report_base_attributes,
    dynamicBaseReady,
    effectiveMetaByName,
    attrMetaById,
    master,
    team,
    currentDeptId,
    currentTeamId,
    clearOnTeamChangeSet,
  ]);

  /* freeWord_* 通知（attr_required:0 の“フリーワード”だけ */
  useEffect(() => {
    const cb = freeWordsChangeRef.current;
    if (!cb) return;

    const dynOrder = allDynamicVisibleSorted
      .filter((a) => isFreeInputManagedAttr(a))
      .map((a) => a.attr_name);

    // 値収集（空でも「項目が存在する」ことは保持）
    const groups = new Map<string, string[]>();
    fields.forEach((f) => {
      if (!f || FIXED_BASE_NAMES.has(f.type))
        return;
      const val = pickValueLike(f.value);
      const arr = groups.get(f.type) ?? [];
      if (val) arr.push(val);
      groups.set(f.type, arr);
    });

    // 空のときもキーを必ず出す（'' を入れる）＋ 互換キー free_* も出す
    const out: Record<string, string | null> = {};
    dynOrder.forEach((name, i) => {
      const idx = i + 1;
      const list = groups.get(name) ?? [];

      if (list.length === 0) {
        out[`freeWord_${idx}`] = '';
        out[`free_${idx}`] = '';
        return;
      }

      out[`freeWord_${idx}`] = list[0] ?? '';
      out[`free_${idx}`] = list[0] ?? '';
      list.forEach((v, j) => {
        out[`freeWord_${idx}_${j}`] = v ?? '';
        out[`free_${idx}_${j}`] = v ?? '';
      });
    });

    cb(out as any);
  }, [fields, allDynamicVisibleSorted]);

  /* ---------- 表示順 ---------- */

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fixedOrder: readonly string[] = [
    'チーム',
    '記入者',
    '連名者',
    '承認-3',
    '承認AM',
  ] as const;
  const commonOrder = useMemo(
    () => visibleCommonAttrsSorted.map((a) => a.attr_name),
    [visibleCommonAttrsSorted]
  );

  const fixedWithDateOrder = useMemo(() => {
    const idx = commonOrder.indexOf(CLASSIFICATION_MARKET_TARGET_ATTR_NAME);
    return idx >= 0
      ? [
          ...fixedOrder,
          ...commonOrder.slice(0, idx + 1),
          '開始終了日付',
          ...commonOrder.slice(idx + 1),
        ]
      : [
          ...fixedOrder,
          '開始終了日付',
          ...commonOrder,
        ];
  }, [fixedOrder, commonOrder]);

  const dynamicSelectOrder = useMemo(
    () =>
      uniqueNames(
        effectiveDynamicAttrsForTeam
          .filter((a) => isSelectType(a.attr_type))
          .map((a) => a.attr_name)
      ),
    [effectiveDynamicAttrsForTeam]
  );
  const dynamicFreeOrder = useMemo(
    () =>
      uniqueNames(
        effectiveDynamicAttrsForTeam
          .filter((a) => isFreeWordType(a.attr_type))
          .map((a) => a.attr_name)
      ),
    [effectiveDynamicAttrsForTeam]
  );

  const dynamicDisplayOrder = useMemo(
    () => prioritizeProcessPlantAttr([...dynamicSelectOrder, ...dynamicFreeOrder]),
    [dynamicSelectOrder, dynamicFreeOrder]
  );

  // ここに固定の以外の選択欄が入る
  const order = useMemo(
    () =>
      uniqueNames([
        ...fixedWithDateOrder,
        ...dynamicDisplayOrder,
      ]),
    [
      fixedWithDateOrder,
      dynamicDisplayOrder,
    ]
  );

  const lastCommonAttrName =
    commonOrder.length > 0
      ? commonOrder[commonOrder.length - 1]
      : null;

  // order に無い型のフォールバック表示
  const extraTypes = useMemo(() => {
    const ordered = new Set<string>(
      order as unknown as string[]
    );
    const allTypes = Array.from<string>(
      new Set<string>(fields.map((f) => String(f.type)))
    );
    return allTypes.filter(
      (t) => !ordered.has(t)
    );
  }, [order, fields]);

  /* 検索値ペイロードの通知（固定 + 動的 “表示値” をそのまま） */
  useEffect(() => {
    if (!onSearchPayloadChange) return;

    const findLabel = (
      opts: { value: string; label: string }[],
      v: string
    ) =>
      (opts.find((o) => o.value === v)?.label ??
        v) || '';

    // 連名者
  const taskMembersValue =
  fields.find((f) => f.type === '連名者' && !f.isRemovable)?.value ?? [];

    const fixed = {
      team: team
        ? {
            value: team,
            label: findLabel(
              uniqueOptions(teamOpts),
              team
            ),
          }
        : undefined,
      reporter: reporter
        ? {
            value: reporter,
            label: findLabel(
              reporterOpts,
              reporter
            ),
          }
        : undefined,
      approver3: approver3
        ? {
            value: approver3,
            label: findLabel(
              approver3Opts,
              approver3
            ),
          }
        : undefined,
      approverAM: approverAM
        ? {
            value: approverAM,
            label: findLabel(
              approverAMOpts,
              approverAM
            ),
          }
        : undefined,
      dateRange: {
        from: startDate || '',
        to: endDate || '',
      },
      // 連名者（旧:課題メンバー）
      taskMembers: Array.isArray(taskMembersValue)
      ? limitTaskMembers(uniqueNormNames(taskMembersValue))
      : [],
    };

    const entries = fields.map<SearchSelectionEntry>((f) => {
      const meta = FIXED_BASE_NAMES.has(f.type)
        ? undefined
        : resolveScopedAttrMeta(f.type, f.attr_id);

      return {
        type: f.type,
        label: f.label ?? f.type,
        display: pickValueLike(f.value),
        raw: f.value,
        attr_id: meta ? toNumberOrNullLocal(meta.attr_id) ?? undefined : undefined,
        isFixed: FIXED_BASE_NAMES.has(f.type),
      };
    });

    const displayMap = entries.reduce<Record<string, string>>((m, e) => {
      m[e.type] = e.display;
      return m;
    }, {});

    const dynamicAttrs: Array<{
      attr_id: number;
      attr_name: string;
      attr_value: string;
    }> = [];

    fields.forEach((f) => {
      if (FIXED_BASE_NAMES.has(f.type)) return;

      const meta = resolveScopedAttrMeta(f.type, f.attr_id);
      const id = toNumberOrNullLocal(meta?.attr_id);
      if (id == null) return;

      const val = pickValueLike(f.value);
      if (!val) return;

      dynamicAttrs.push({
        attr_id: id,
        attr_name: meta?.attr_name ?? f.type,
        attr_value: val,
      });
    });

    onSearchPayloadChange({
      displayMap,
      entries,
      fixed,
      dynamicAttrs,
      meta: {
        teamCleared: team === '',
        teamClearedByUser:
          userTouchedTeamRef.current &&
          team === '',
      },
      extras: {
        reportType,
        shikimuke,
        lot,
        powertrain,
        project,
        teamCleared: team === '',
        teamClearedByUser:
          userTouchedTeamRef.current &&
          team === '',
      },
    });
  }, [
    onSearchPayloadChange,
    fields,
    team,
    reporter,
    approver3,
    approverAM,
    startDate,
    endDate,
    teamOpts,
    reporterOpts,
    approver3Opts,
    approverAMOpts,
    resolveScopedAttrMeta,
    reportType,
    shikimuke,
    lot,
    powertrain,
    project,
  ]);

  /* ---------- 描画 ---------- */
  return (
    <div>
      <div className="search-areas">
        <div className="search-area-input">
          {Array.from(order).map((type) => {
            // チームは固定＋追加分の2段
            if (type === 'チーム') {
              const added = fields.filter(
                (f) =>
                  f.type === 'チーム' &&
                  f.isRemovable
              );
              const teamOptions = withEmpty(
                uniqueOptions(teamOpts),
                'チーム'
              );
              const selectedTeam =
                teamOptions.find(
                  (o) => o.value === team
                ) ?? teamOptions[0];

              return (
                <React.Fragment key="team-row-group">
                  <div
                    className="field-group field-group-team"
                  >
                    <div className="form-field select-field team">
                      <div className="team-label-row">
                        <label htmlFor="team-static">
                          チーム&nbsp;
                          {SyncIcon}
                        </label>
                        <label className="team-past-toggle">
                          <input
                            type="checkbox"
                            checked={showPastTeams}
                            onChange={(e) => setShowPastTeams(e.target.checked)}
                          />
                          過去チームも表示
                        </label>
                      </div>
                      <CommonSelect
                        id="team-static"
                        options={teamOptions}
                        value={
                          selectedTeam?.value
                            ? selectedTeam
                            : null
                        }
                        placeholder="-- チーム --"
                        {...({
                          onChange: (
                            opt: any
                          ) => {
                            userTouchedTeamRef.current =
                              true;
                            const v = norm(
                              opt?.value ??
                                ''
                            );
                            setTeam(v);
                            onTeamChange?.(
                              v === ''
                                ? null
                                : v
                            );
                          },
                        } as any)}
                      />
                    </div>
                    {added.map((f) => (
                      <div
                        key={f.id}
                        className={`form-field dynamic-field-wrapper add-parent-${clsNameOf(
                          'チーム'
                        )}`}
                      >
                        {wrapField(f)}
                        {f.isRemovable && (
                          <button
                            className="delete-button"
                            type="button"
                            onClick={() =>
                              setFields(
                                (p) =>
                                  p.filter(
                                    (
                                      x
                                    ) =>
                                      x.id !==
                                      f.id
                                  )
                              )
                            }
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </React.Fragment>
              );
            }

            if (type === '記入者') {
              const base = fields.find(
                (f) =>
                  f.type === '記入者' &&
                  !f.isRemovable
              );
              const add = fields.filter(
                (f) =>
                  f.type === '記入者' &&
                  f.isRemovable
              );

              // ★連名者は「記入者」グループの中でだけ描画する
              const baseTask = fields.find(
                (f) =>
                  f.type === '連名者' &&
                  !f.isRemovable
              );
              const addTask = fields.filter(
                (f) =>
                  f.type === '連名者' &&
                  f.isRemovable
              );

              return (
                <div
                  key="reporter-and-task-group"
                  className="field-group field-group-reporter"
                >
                  {base && wrapField(base)}
                  {add.map(wrapField)}

                  {baseTask && wrapField(baseTask)}
                  {addTask.map(wrapField)}
                </div>
              );
            }

            // 連名者は「記入者」グループで描画済みなので、ここでは描画しない
            if (type === '連名者') return null;

            if (type === '承認-3') {
              const added = fields.filter(
                (f) =>
                  f.type === '承認-3' &&
                  f.isRemovable
              );
              const apprOptions = withEmpty(
                uniqueOptions(approver3Opts),
                APPROVER3_DISPLAY_LABEL
              );
              const selectedAppr =
                apprOptions.find(
                  (o) =>
                    o.value ===
                    approver3
                ) ?? apprOptions[0];

              return (
                <div
                  key="approver3-group"
                  className="field-group field-group-approver3"
                >
                  <div className="form-field select-field approver3">
                    <label htmlFor="approver3-static">
                      {APPROVER3_DISPLAY_LABEL}&nbsp;
                      {SyncIcon}
                    </label>
                    <CommonSelect
                      id="approver3-static"
                      options={apprOptions}
                      value={
                        selectedAppr?.value
                          ? selectedAppr
                          : null
                      }
                      placeholder={`-- ${APPROVER3_DISPLAY_LABEL} --`}
                      {...({
                        onChange: (
                          opt: any
                        ) => {
                          const v = norm(
                            opt?.value ??
                              ''
                          );
                          setApprover3(v);
                          onApprover3Change?.(
                            v
                          );
                        },
                      } as any)}
                    />
                  </div>
                  {added.map(wrapField)}
                </div>
              );
            }

            if (type === '承認AM') {
              const amBase = fields.find(
                (f) =>
                  f.type === '承認AM' &&
                  !f.isRemovable
              );
              const amAdds = fields.filter(
                (f) =>
                  f.type === '承認AM' &&
                  f.isRemovable
              );
              if (!amBase && amAdds.length === 0)
                return null;
              return (
                <div
                  key="approver-am-group"
                  className="field-group field-group-approver-am"
                >
                  {amBase && wrapField(amBase)}
                  {amAdds.map(wrapField)}
                </div>
              );
            }

            if (type === '開始終了日付') {
              const base = fields.find(
                (f) =>
                  f.type ===
                    '開始終了日付' &&
                  !f.isRemovable
              );
              return base ? (
                <div
                  key="daterange"
                  className={`field-group field-group-${clsNameOf(
                    type
                  )}`}
                >
                  {wrapField(base)}
                </div>
              ) : null;
            }

            // 動的属性
            const base = fields.find(
              (f) =>
                f.type === type &&
                !f.isRemovable
            );
            const adds = fields.filter(
              (f) =>
                f.type === type &&
                f.isRemovable
            );
            if (!base && adds.length === 0)
              return null;

            const content = (
              <div
                key={type}
                className={`field-group field-group-${clsNameOf(
                  String(type)
                )}`}
              >
                {/* /Projectなどを描画 */}
                {base && wrapField(base)}
                {adds.map(wrapField)}
              </div>
            );

            if (
              type === lastCommonAttrName
            ) {
              return (
                <React.Fragment
                  key={`${type}-with-break`}
                >
                  {content}
                  <div
                    className="row-break-after-common"
                    style={{
                      gridColumn:
                        '1 / -1',
                      width: '100%',
                      height: 0,
                      margin: 0,
                      padding: 0,
                    }}
                  />
                </React.Fragment>
              );
            }

            return content;
          })}

          {/* order に無い型もフォールバック描画（安全網） */}
          {extraTypes.map((type) => {
            if (
              FIXED_BASE_NAMES.has(type)
            )
              return null;
            const base = fields.find(
              (f) =>
                f.type === type &&
                !f.isRemovable
            );
            const adds = fields.filter(
              (f) =>
                f.type === type &&
                f.isRemovable
            );
            if (!base && adds.length === 0)
              return null;
            return (
              <div
                key={`extra-${type}`}
                className={`field-group field-group-${clsNameOf(
                  String(type)
                )}`}
              >
                {base && wrapField(base)}
                {adds.map(wrapField)}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OverwriteInputSearchArea;
